import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import ContentPasteRoundedIcon from '@mui/icons-material/ContentPasteRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SaveAsRoundedIcon from '@mui/icons-material/SaveAsRounded'
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded'
import type { ChecklistItem as LegacyChecklistItem, Goal, SubTask, Task, TimelineDate } from '../../types/timeline'
import { useTimeline } from '../../context/useTimeline'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type CreateTimelineElementInput, type TimelineElement, type TimelineElementType } from '../../context/TimelineContext'
import type { ChecklistItem as FlatChecklistItem, TimelineBoardKey } from '../../types/schema'
import type { WorkspaceKind } from '../../context/WorkspaceContext'
import { useWorkspace } from '../../context/useWorkspace'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import type { AiOperationalProposalMetadata } from '../../services/ai/proposals/operationalProposalAdapter'
import type { BoardGroup, NodeDragPayload, PasteTarget } from './types'
import { decideLiveReviewSync } from './liveReviewSync'
import { useData } from '../../context/useData'
import { useToast } from '../../context/useToast'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNls } from '../../nls/nlsProvider'
import { canSaveNode } from '../../utils/saveOrderGuard'
import { toSafeIso } from '../../utils/safeDate'
import { validateGoalTree, type ValidationIssue } from '../../utils/timelineValidators'
import { buildTree, buildWorkspaceTree, findSelectionForNodeId, resolveSelection, resolveSelectionInWorkspace } from './diff'
import { FolderTree, TreeContextMenu, treeSelectionKey } from './FolderTree'
import { GoalLikeForm } from './GoalLikeForm'
import { ManageChecklistDetailSurface } from './ManageChecklistDetailSurface'
import { ItemAttachmentsPanel } from '../Forms/ItemAttachmentsPanel'
import { NodeBudgetLimitCard } from '../Budget/NodeBudgetLimitCard'
import { ExternalLinkSidecard } from '../LinkHub/shared/ExternalLinkSidecard'
import { attachFigmaFile, createGitHubIssue } from '../../services/linkHubConnectionService'
import { ConfirmDialog } from './ConfirmDialog'
import { BinMoveDialog } from './BinMoveDialog'
import { AppDialog, type AppDialogAction } from '../Common/AppDialog'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { FilterChip, RailChipTabs, TextInput } from '../Common'
import type { ButtonGroupItem } from '../Common/ButtonGroup'
import { useOptionalManageDraft } from '../../context/manageDraftContextCore'
import { usePessimisticLock } from '../../hooks/usePessimisticLock'
import { useAuth } from '../../context/useAuth'
import { isRowLockedByPeer } from '../manageRowLock'
import { getManageBoardLabel, getManageSaveDiscardLabels } from '../Manage/ManageNls'
import { setManageActionsBar } from '../Manage/manageActionsBarStore'
import {
  applyChecklistMove,
  buildDragPayload,
  collectAllGoalLikeIds,
  collectSubtreeIds,
  findGoalLikeAncestors,
  isValidDrop,
} from './treeOps'
import { applyPaste, canPasteAt, resolveClipboardEntry } from './clipboardOps'
import type {
  ChecklistDragPayload,
  ChecklistDropTarget,
  ClipboardEntry,
  DetailedSelection,
  DetailedViewMode,
  DiscardScope,
  SaveScope,
  TreeNode,
} from './types'

export function ensureValidSelection(
  currentSel: DetailedSelection | null,
  goals: Goal[],
): DetailedSelection | null {
  if (!goals || goals.length === 0) return null
  if (!currentSel) return { kind: 'goal', goalId: goals[0].id }

  if (currentSel.kind === 'goal') {
    if (goals.some((g) => g.id === currentSel.goalId)) {
      return currentSel
    }
    return { kind: 'goal', goalId: goals[0].id }
  }

  if (currentSel.kind === 'task') {
    const parentGoal = goals.find((g) => g.id === currentSel.goalId)
    if (parentGoal) {
      if (parentGoal.tasks.some((t) => t.id === currentSel.taskId)) {
        return currentSel
      }
      return { kind: 'goal', goalId: parentGoal.id }
    }
    return { kind: 'goal', goalId: goals[0].id }
  }

  if (currentSel.kind === 'subtask') {
    const parentGoal = goals.find((g) => g.id === currentSel.goalId)
    if (parentGoal) {
      const parentTask = parentGoal.tasks.find((t) => t.id === currentSel.taskId)
      if (parentTask) {
        if (parentTask.subTasks.some((s) => s.id === currentSel.subTaskId)) {
          return currentSel
        }
        return { kind: 'task', goalId: parentGoal.id, taskId: parentTask.id }
      }
      return { kind: 'goal', goalId: parentGoal.id }
    }
    return { kind: 'goal', goalId: goals[0].id }
  }

  if (currentSel.kind === 'checklist') {
    const parentGoal = goals.find((g) => g.id === currentSel.goalId)
    if (parentGoal) {
      const parentTask = parentGoal.tasks.find((t) => t.id === currentSel.taskId)
      if (parentTask) {
        if (parentTask.checklist.some((c) => c.id === currentSel.checklistId)) {
          return currentSel
        }
        return { kind: 'task', goalId: parentGoal.id, taskId: parentTask.id }
      }
      return { kind: 'goal', goalId: parentGoal.id }
    }
    return { kind: 'goal', goalId: goals[0].id }
  }

  return { kind: 'goal', goalId: goals[0].id }
}

interface DetailedViewScreenProps {
  isOpen: boolean
  mode: DetailedViewMode
  /** Single-goal mode props (used by AI Generate / AI Edit / Detail flows). */
  workingGoal: Goal | null
  setWorkingGoal: (next: Goal | null) => void
  baselineGoal: Goal | null
  /**
   * Workspace mode props — set by Manage's "Advanced" entry. When `mode` is
   * 'workspace', `workingGoals` + `setWorkingGoals` + `baselineGoals` are the
   * source of truth and the single-goal props above are ignored. The screen
   * still operates internally on goal arrays even in single-goal modes, so
   * features (diff / save / clipboard) stay uniform.
   */
  workingGoals?: Goal[] | null
  setWorkingGoals?: ((next: Goal[]) => void) | null
  baselineGoals?: Goal[] | null
  /** Initial focus selection — used by workspace mode to auto-uncollapse the
   * Manage-selected node's ancestor chain. */
  initialFocus?: DetailedSelection | null
  /** Issues produced by post-flight validation when the AI flow returned.
   * The screen re-validates live as the user edits, but we accept the
   * initial set so the first paint already shows ⚠️ markers without a
   * one-frame delay. */
  initialValidationIssues?: unknown[] | null
  /** Free-text commentary from the AI (per-target refusals, etc.) shown as
   * a banner at the top of Manage. */
  aiNote?: string | null
  /** Local ids the AI freshly created during this run. Drives "+ new" marker chips. */
  aiNewIds?: string[] | null
  /** Local ids the AI actually changed during this run. Drives the asterisk
   * + draft-attention affordance — items the AI did NOT touch don't show
   * either marker. Null means generate-mode (every node is a draft). */
  aiChangedIds?: string[] | null
  aiConfidence?: number | null
  aiOperationalProposal?: AiOperationalProposalMetadata | null
  /** Outer shell. Manage is the single timeline draft/edit surface. */
  chrome?: 'manage'
  /** Current board the user is viewing. In Manage chrome the tree exposes
   * board folders as top-level nodes — this one is expanded by default and is
   * the implicit target for newly-created goals. */
  activeBoard?: TimelineBoardKey
  /** Workspace kind — personal mode collapses the board-folder layer to just
   * `DASHBOARD`. */
  appMode?: WorkspaceKind
  headerControls?: ReactNode
  /** Notify parent of selection changes and whether the selected node is expanded in the tree. */
  onSelectionChange?: (selection: DetailedSelection | null, isExpanded: boolean) => void
  /**
   * The current Manage mode. When 'review', this component publishes its
   * own action set to the shared store and the top chrome shows Review
   * actions. When 'board' or undefined, Board owns the store publish.
   */
  manageMode?: 'board' | 'review'
  /**
   * Workspace mode only: the goals as they are live right now (elements +
   * checklist items). With no unsaved edits, Review rebases onto them via
   * `onRebaseToLive`; with unsaved edits it keeps the draft and offers a
   * discard-and-refresh notice (H-1).
   */
  liveGoals?: Goal[] | null
  onRebaseToLive?: (goals: Goal[]) => void
}

export type ReviewHeadingContentProps = {
  chrome?: 'drawer' | 'page' | 'manage'
  goalsList?: Goal[]
  isWorkspaceMode?: boolean
}

export const ReviewHeadingContent = ({
  chrome = 'manage',
  goalsList = [],
  isWorkspaceMode = true,
}: ReviewHeadingContentProps) => {
  const { t } = useNls()
  const isManageChrome = chrome === 'manage'

  return (
    <div className="reviewHeadingContent">
      <h2>
        {isManageChrome ? t('NLSManageTitleReview') : t('NLSManageTitleManage')}
      </h2>
      {!isManageChrome && !isWorkspaceMode && goalsList[0] && (
        <p className="detailedViewSubtitle">{goalsList[0].title}</p>
      )}
      {!isManageChrome && isWorkspaceMode && (
        <p className="detailedViewSubtitle">
          {goalsList.length} goal{goalsList.length === 1 ? '' : 's'} in workspace
        </p>
      )}
    </div>
  )
}

type PersistedDraftNode = Pick<TimelineElement, 'id' | 'type' | 'parentId' | 'board' | 'routePath'>

interface PersistBatchResult {
  savedLocalIds: string[]
  savedNodesByLocal: Record<string, PersistedDraftNode>
}

const ISO_DATE = (value: string | number): string => toSafeIso(value)

const ROOT_PREFIX = 'board-root:'

const checklistRootId = (workspaceId: string, board: TimelineBoardKey) =>
  board === 'DASHBOARD' ? WORKSPACE_DEFAULT_CHECKLIST_ID : `${ROOT_PREFIX}${workspaceId}:${board}`

const buildChecklistTreeNode = (
  item: FlatChecklistItem,
  boardRootId: string,
  invalidIds: Set<string>,
): TreeNode => ({
  id: item.id,
  kind: 'checklist',
  title: item.title || 'Untitled item',
  diff: 'unchanged',
  unsaved: false,
  selectable: true,
  selection: {
    kind: 'checklist',
    goalId: boardRootId,
    taskId: null,
    subTaskId: null,
    checklistId: item.id,
  },
  children: [],
  hasDiffDescendant: false,
  validation: invalidIds.has(item.id) ? 'invalid' : 'ok',
  needsDraftAttention: false,
})

const newLocalId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`

const MANUAL_COLORS = ['#22c55e', '#38bdf8', '#facc15', '#a78bfa', '#fb7185', '#34d399'] as const // mosaic-keep: user item palette, assigned by index

const colorAt = (index: number): `#${string}` => MANUAL_COLORS[index % MANUAL_COLORS.length]

const addDaysIso = (value: string, days: number): string => {
  const date = new Date(value)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString()
}

const createManualGoal = (index: number): Goal => {
  const startDate = new Date().toISOString()
  return {
    id: newLocalId(),
    title: `New goal ${index + 1}`,
    description: '',
    checklist: [],
    startDate,
    endDate: addDaysIso(startDate, 30),
    baseColor: colorAt(index),
    tasks: [],
  }
}

const createManualTask = (goal: Goal): Task => {
  const startDate = ISO_DATE(goal.startDate)
  const endDate = ISO_DATE(goal.endDate)
  return {
    id: newLocalId(),
    title: `New task ${goal.tasks.length + 1}`,
    description: '',
    checklist: [],
    startDate,
    endDate,
    color: colorAt(goal.tasks.length + 1),
    subTasks: [],
  }
}

const createManualSubTask = (task: Task): SubTask => {
  const startDate = ISO_DATE(task.startDate)
  const endDate = ISO_DATE(task.endDate)
  return {
    id: newLocalId(),
    title: `New subtask ${task.subTasks.length + 1}`,
    description: '',
    checklist: [],
    startDate,
    endDate,
    color: colorAt(task.subTasks.length + 2),
  }
}

const createManualChecklist = (index: number): LegacyChecklistItem => ({
  id: newLocalId(),
  text: `New checklist file ${index + 1}`,
  description: '',
  isCompleted: false,
  weightage: 1,
  complexityStars: 3,
  urgencyStars: 3,
  importanceStars: 3,
  estimatedHours: null,
  actualCost: null,
  currencyCode: 'USD',
  scheduleMode: 'DATED',
  workflowStatus: 'CREATED',
  isHelperVisible: true,
})

// ---- Tree mutation helpers (immutable updates to the working goal) --------

const replaceGoalLike = <T extends Goal | Task | SubTask>(item: T, patch: Partial<T>): T => ({
  ...item,
  ...patch,
})

const updateGoalNode = (
  goal: Goal,
  selection: DetailedSelection,
  patch: Goal | Task | SubTask | LegacyChecklistItem,
): Goal => {
  if (selection.kind === 'goal') return replaceGoalLike(goal, patch as Partial<Goal>)

  if (selection.kind === 'task') {
    return {
      ...goal,
      tasks: goal.tasks.map((task) => (task.id === selection.taskId ? (patch as Task) : task)),
    }
  }

  if (selection.kind === 'subtask') {
    return {
      ...goal,
      tasks: goal.tasks.map((task) =>
        task.id === selection.taskId
          ? {
              ...task,
              subTasks: task.subTasks.map((sub) => (sub.id === selection.subTaskId ? (patch as SubTask) : sub)),
            }
          : task,
      ),
    }
  }

  const patchedItem = patch as LegacyChecklistItem
  const replaceInList = (items: LegacyChecklistItem[]) =>
    items.map((entry) => (entry.id === selection.checklistId ? patchedItem : entry))

  if (!selection.taskId) {
    return { ...goal, checklist: replaceInList(goal.checklist) }
  }
  return {
    ...goal,
    tasks: goal.tasks.map((task) => {
      if (task.id !== selection.taskId) return task
      if (!selection.subTaskId) return { ...task, checklist: replaceInList(task.checklist) }
      return {
        ...task,
        subTasks: task.subTasks.map((sub) =>
          sub.id === selection.subTaskId ? { ...sub, checklist: replaceInList(sub.checklist) } : sub,
        ),
      }
    }),
  }
}

// ---- Build Firestore element input from a working-goal node ---------------

const findParentInfo = (
  goal: Goal,
  nodeId: string,
): { kind: 'goal' | 'task' | 'subtask'; node: Goal | Task | SubTask; parentId: string | null } | null => {
  if (goal.id === nodeId) return { kind: 'goal', node: goal, parentId: null }
  for (const task of goal.tasks) {
    if (task.id === nodeId) return { kind: 'task', node: task, parentId: goal.id }
    for (const sub of task.subTasks) {
      if (sub.id === nodeId) return { kind: 'subtask', node: sub, parentId: task.id }
    }
  }
  return null
}

/**
 * Return a copy of `goal` where the node with `nodeId` is replaced by
 * `snapshot`. Children of replaced nodes are preserved from the snapshot
 * (cut/paste etc. happen at the snapshot's own structure). Used when
 * persisting a marked-for-save node so the snapshot wins over later edits.
 */
const overrideNodeInGoal = (
  goal: Goal,
  nodeId: string,
  snapshot: Goal | Task | SubTask,
): Goal => {
  if (goal.id === nodeId) {
    const snap = snapshot as Goal
    return {
      ...goal,
      title: snap.title,
      description: snap.description,
      startDate: snap.startDate,
      endDate: snap.endDate,
      baseColor: snap.baseColor,
      checklist: snap.checklist,
    }
  }
  return {
    ...goal,
    tasks: goal.tasks.map((task) => {
      if (task.id === nodeId) {
        const snap = snapshot as Task
        return {
          ...task,
          title: snap.title,
          description: snap.description,
          startDate: snap.startDate,
          endDate: snap.endDate,
          color: snap.color,
          checklist: snap.checklist,
        }
      }
      return {
        ...task,
        subTasks: task.subTasks.map((sub) => {
          if (sub.id !== nodeId) return sub
          const snap = snapshot as SubTask
          return {
            ...sub,
            title: snap.title,
            description: snap.description,
            startDate: snap.startDate,
            endDate: snap.endDate,
            color: snap.color,
            checklist: snap.checklist,
          }
        }),
      }
    }),
  }
}

const buildElementInput = (
  goal: Goal,
  nodeId: string,
  savedElementIdsByLocal: Record<string, string>,
  targetBoard?: TimelineBoardKey,
): CreateTimelineElementInput | null => {
  const info = findParentInfo(goal, nodeId)
  if (!info) return null

  const node = info.node
  const startDate = ISO_DATE(node.startDate)
  const endDate = ISO_DATE(node.endDate)

  if (info.kind === 'goal') {
    const goalNode = node as Goal
    return {
      id: goalNode.id,
      type: 'GOAL',
      parentId: null,
      title: goalNode.title,
      description: goalNode.description,
      checklist: goalNode.checklist,
      weight: 1,
      progress: 0,
      startDate,
      endDate,
      color: goalNode.baseColor,
      ...(targetBoard ? { board: targetBoard } : {}),
    }
  }

  const resolvedParentId = savedElementIdsByLocal[info.parentId!] ?? info.parentId
  if (!resolvedParentId) return null

  if (info.kind === 'task') {
    const taskNode = node as Task
    return {
      id: taskNode.id,
      type: 'TASK',
      parentId: resolvedParentId,
      title: taskNode.title,
      description: taskNode.description,
      checklist: taskNode.checklist,
      weight: 1,
      progress: 0,
      startDate,
      endDate,
      color: taskNode.color,
    }
  }

  const subNode = node as SubTask
  return {
    id: subNode.id,
    type: 'SUBTASK',
    parentId: resolvedParentId,
    title: subNode.title,
    description: subNode.description,
    checklist: subNode.checklist,
    weight: 1,
    progress: 0,
    startDate,
    endDate,
    color: subNode.color,
  }
}

// ---- Discard prompt copy --------------------------------------------------

interface PendingDiscard {
  scope: DiscardScope
  title: string
  body: string
}

const buildDiscardPrompt = (
  scope: DiscardScope,
  workingGoal: Goal | null,
  selection: DetailedSelection | null,
  t: ReturnType<typeof useNls>['t'],
): PendingDiscard => {
  if (scope.kind === 'all-unsaved') {
    return {
      scope,
      title: t('NLSManageDialogDiscardAllUnsavedTitle'),
      body: t('NLSManageDialogDiscardAllUnsavedBody'),
    }
  }
  if (scope.kind === 'all-session') {
    return {
      scope,
      title: t('NLSManageDialogDiscardAllSessionTitle'),
      body: t('NLSManageDialogDiscardAllSessionBody'),
    }
  }
  // self (cascading).
  const noun =
    selection?.kind === 'goal'
      ? t('NLSManageKindGoal')
      : selection?.kind === 'task'
        ? t('NLSManageKindTask')
        : selection?.kind === 'subtask'
          ? t('NLSManageKindSubtask')
          : t('NLSManageKindItem')
  const cascadeNote =
    selection?.kind === 'goal'
      ? t('NLSManageDialogDiscardSelfCascadeGoal')
      : selection?.kind === 'task'
        ? t('NLSManageDialogDiscardSelfCascadeTask')
        : ''
  const _ = workingGoal // referenced for future per-name copy; silence unused
  void _
  return {
    scope,
    title: t('NLSManageDialogDiscardSelfTitle', { noun }),
    body: t('NLSManageDialogDiscardSelfBody', { noun, cascadeNote }),
  }
}

// ---- Component ------------------------------------------------------------

export const DetailedViewScreen = ({
  isOpen,
  mode,
  workingGoal,
  setWorkingGoal,
  baselineGoal,
  workingGoals,
  setWorkingGoals,
  baselineGoals,
  initialFocus,
  initialValidationIssues,
  aiNote,
  aiNewIds,
  aiChangedIds,
  aiConfidence = null,
  aiOperationalProposal = null,
  chrome = 'manage',
  activeBoard = 'DASHBOARD',
  appMode = 'TEAM',
  headerControls,
  onSelectionChange,
  manageMode = 'review',
  liveGoals = null,
  onRebaseToLive,
}: DetailedViewScreenProps) => {
  const isWorkspaceMode = mode === 'workspace'
  const { elements, checklistItems, createElement, updateElement, deleteElement, permanentlyDeleteElement, canPerformOnNode } = useTimeline()
  const { showToast } = useToast()
  const { t } = useNls()
  const { activeWorkspaceId, activeRole, workspaces } = useWorkspace()
  const activeWorkspace = useMemo(() => workspaces.find((w) => w.id === activeWorkspaceId) ?? null, [activeWorkspaceId, workspaces])
  const { currentUser } = useAuth()
  useData()
  // Pessimistic edit locks — mirror BoardExplorer's usage so both Manage
  // sub-views share the same workspace-generic lease system for timeline
  // elements. Any id in Review's `unsavedIds` gets a lease; leases release
  // on save / discard / unmount.
  const { acquireEditLease, releaseLease, isLockedByExternalUser, getExternalLock } = usePessimisticLock()
  const heldLeasesRef = useRef<Set<string>>(new Set())

  const getLockStatus = useCallback(
    (id: string) => isRowLockedByPeer(getExternalLock(id), currentUser?.uid),
    [getExternalLock, currentUser?.uid],
  )

  // ---- Unified goal list (works for single AND workspace modes) ----------
  // The internal logic always operates on an array of goals; single-goal
  // callers contribute [workingGoal]. Updates flow back through setGoalAt.
  const goalsList: Goal[] = useMemo(
    () => (
      isWorkspaceMode
        ? workingGoals ?? []
        : workingGoal
          ? [workingGoal]
          : []
    ),
    [isWorkspaceMode, workingGoals, workingGoal],
  )
  const baselineList: Goal[] = useMemo(
    () => (
      isWorkspaceMode
        ? baselineGoals ?? []
        : baselineGoal
          ? [baselineGoal]
          : []
    ),
    [isWorkspaceMode, baselineGoals, baselineGoal],
  )

  const setGoalAt = useCallback(
    (goalId: string, updater: (goal: Goal) => Goal) => {
      if (isWorkspaceMode) {
        if (!setWorkingGoals) return
        setWorkingGoals(
          (workingGoals ?? []).map((goal) => (goal.id === goalId ? updater(goal) : goal)),
        )
        return
      }
      if (workingGoal && workingGoal.id === goalId) setWorkingGoal(updater(workingGoal))
    },
    [isWorkspaceMode, setWorkingGoals, workingGoals, setWorkingGoal, workingGoal],
  )

  const [selection, setSelection] = useState<DetailedSelection | null>(() => initialFocus ?? null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // In-memory markers for AI created ("new") and edited ("edited") items.
  // These survive Board <-> Review switches and clear on user selection.
  const [newMarkerIds, setNewMarkerIds] = useState<Set<string>>(() => new Set(aiNewIds ?? []))
  const [editedMarkerIds, setEditedMarkerIds] = useState<Set<string>>(() => new Set(aiChangedIds ?? []))

  useEffect(() => {
    if (aiNewIds && aiNewIds.length > 0) {
      setNewMarkerIds(new Set(aiNewIds))
    }
  }, [aiNewIds])

  useEffect(() => {
    if (aiChangedIds && aiChangedIds.length > 0) {
      setEditedMarkerIds(new Set(aiChangedIds))
    }
  }, [aiChangedIds])

  const handleTreeSelect = useCallback((sel: DetailedSelection) => {
    const selectedId = sel.kind === 'goal'
      ? sel.goalId
      : sel.kind === 'task'
        ? sel.taskId
        : sel.kind === 'subtask'
          ? sel.subTaskId
          : sel.checklistId
    if (selectedId) {
      setNewMarkerIds((prev) => {
        if (!prev.has(selectedId)) return prev
        const next = new Set(prev)
        next.delete(selectedId)
        return next
      })
      setEditedMarkerIds((prev) => {
        if (!prev.has(selectedId)) return prev
        const next = new Set(prev)
        next.delete(selectedId)
        return next
      })
    }
    setSelection(sel)
  }, [])

  // Runs when App re-seeds initialFocus (mirror from Board->Review). The
  // goal-key-based initializer at line 775 only fires on a fresh goal set,
  // so a re-seed within the same goal set needs this second hook.
  useEffect(() => {
    if (initialFocus) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelection(initialFocus)
    }
  }, [initialFocus])

  useEffect(() => {
    if (!onSelectionChange) return
    const selectedId = selection
      ? selection.kind === 'goal'
        ? selection.goalId
        : selection.kind === 'task'
          ? selection.taskId
          : selection.kind === 'subtask'
            ? selection.subTaskId
            : selection.checklistId
      : null
    const isExpanded = Boolean(selectedId && expandedIds.has(selectedId))
    onSelectionChange(selection, isExpanded)
  }, [selection, expandedIds, onSelectionChange])

  const selectedElementId = selection
    ? selection.kind === 'goal'
      ? selection.goalId
      : selection.kind === 'task'
        ? selection.taskId
        : selection.kind === 'subtask'
          ? selection.subTaskId
          : null
    : null

  const [liveElementDoc, setLiveElementDoc] = useState<Partial<TimelineElement> | null>(null)
  useEffect(() => {
    if (!selectedElementId) {
      setLiveElementDoc(null)
      return
    }
    const docRef = doc(firestoreDb, 'elements', selectedElementId)
    return onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setLiveElementDoc({ id: snapshot.id, ...(snapshot.data() as object) } as Partial<TimelineElement>)
      }
    }, (error) => {
      console.warn('[DetailedViewScreen] failed to subscribe to element doc', error)
    })
  }, [selectedElementId])

  const [unsavedIds, setUnsavedIds] = useState<Set<string>>(new Set())
  /** Local ids the AI changed AND the user hasn't yet "accepted" or saved.
   * Drives both the asterisk and the per-row Accept tick in edit mode. */
  const [draftAttentionIds, setDraftAttentionIds] = useState<Set<string>>(new Set())
  /** Banner message — initially the AI's commentary, dismissible. */
  const [bannerNote, setBannerNote] = useState<string | null>(null)
  /** Internal clipboard. In workspace+manage chrome goals are allowed too
   * (used for cross-board moves); single-goal modes still toast when the
   * user tries to copy a goal — captureClipboard enforces that. */
  const [clipboardEntry, setClipboardEntry] = useState<ClipboardEntry | null>(null)
  /** Per-node target board overrides — populated by cross-board cut/paste
   * and cross-board drag. On commit, persistNode uses this to stamp the
   * correct board on createElement / updateElement. */
  const [nodeBoardOverrides, setNodeBoardOverrides] = useState<Map<string, TimelineBoardKey>>(new Map())
  /** Pending right-click context menu — rendered as a floating menu near the
   * triggering row. Cleared when an action runs or the user clicks away. */
  const [contextMenu, setContextMenu] = useState<{
    selection: DetailedSelection
    position: { clientX: number; clientY: number }
  } | null>(null)
  /** Selection pending a permanent-delete confirmation (Bin only). */
  const [pendingPermanentDelete, setPendingPermanentDelete] = useState<DetailedSelection | null>(null)
  /**
   * Goal/Task/Subtask ids that have been committed (or pre-existed) in
   * Firestore — the gating set used by `canSaveNode`. In edit mode this starts
   * with every baseline id since the user is editing nodes that already exist.
   */
  const [savedNodeIds, setSavedNodeIds] = useState<Set<string>>(new Set())
  const [, setMarkedForSaveIds] = useState<Set<string>>(new Set())
  /** Per-node snapshot of working state captured at the moment of clicking
   * Save (deferred mode). On Close → commit, persist uses this snapshot
   * (not the latest working state). On Discard → revert in-memory to this
   * snapshot (not the original baseline). Re-clicking Save updates it. */
  const [markedSaveSnapshots, setMarkedSaveSnapshots] = useState<
    Map<string, Goal | Task | SubTask>
  >(new Map())
  const [savedElementIdsByLocal, setSavedElementIdsByLocal] = useState<Record<string, string>>({})
  const [sessionUndoSnapshots, setSessionUndoSnapshots] = useState<Record<string, TimelineElement | null>>({})
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(null)
  const [binMoveElementId, setBinMoveElementId] = useState<string | null>(null)
  const [figmaPromptOpen, setFigmaPromptOpen] = useState(false)
  const [figmaUrlInput, setFigmaUrlInput] = useState('')
  const [figmaTargetTask, setFigmaTargetTask] = useState<{ workspaceId: string; elementId: string } | null>(null)

  const handleAttachFigma = useCallback(() => {
    if (!figmaTargetTask || !figmaUrlInput.trim()) return
    const { workspaceId, elementId } = figmaTargetTask
    const fileUrl = figmaUrlInput.trim()
    setFigmaPromptOpen(false)
    setFigmaTargetTask(null)
    setFigmaUrlInput('')
    void attachFigmaFile(workspaceId, elementId, fileUrl)
      .then(() => showToast(t('NLSDetailedViewFigmaAttachedToast'), 'success'))
      .catch((error: unknown) =>
        showToast(error instanceof Error ? error.message : t('NLSDetailedViewFigmaAttachFailedToast'), 'error'),
      )
  }, [figmaTargetTask, figmaUrlInput, showToast, t])

  const figmaActions: AppDialogAction[] = useMemo(() => [
    {
      label: t('NLSCommonActionCancel'),
      variant: 'secondary',
      onClick: () => {
        setFigmaPromptOpen(false)
        setFigmaTargetTask(null)
        setFigmaUrlInput('')
      },
    },
    {
      label: t('NLSDetailedViewFigmaDialogAttach'),
      variant: 'primary',
      disabled: !figmaUrlInput.trim(),
      onClick: handleAttachFigma,
    },
  ], [figmaUrlInput, handleAttachFigma, t])
  const { isMobileViewport } = useClientPreferences()
  const [mobileTab, setMobileTab] = useState<'tree' | 'baseline' | 'proposed'>('tree')
  const [treeColPct, setTreeColPct] = useState<number>(() => {
    try {
      const raw = localStorage.getItem('detailedView:treeColPct')
      if (raw) {
        const parsed = parseFloat(raw)
        if (!Number.isNaN(parsed)) {
          return Math.min(32, Math.max(16, parsed))
        }
      }
    } catch {
      /* ignore */
    }
    return 20
  })
  const [isDraggingTreeResizer, setIsDraggingTreeResizer] = useState(false)

  const handleTreeResizerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsDraggingTreeResizer(true)
    let rafId: number | null = null
    let latestPct = treeColPct

    const onPointerMove = (moveEvt: PointerEvent) => {
      if (rafId != null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        const bodyWidth = document.body.clientWidth || window.innerWidth
        if (bodyWidth <= 0) return
        const rawPct = (moveEvt.clientX / bodyWidth) * 100
        const clamped = Math.min(32, Math.max(16, rawPct))
        latestPct = clamped
        setTreeColPct(clamped)
      })
    }

    const onPointerUp = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
      setIsDraggingTreeResizer(false)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      try {
        localStorage.setItem('detailedView:treeColPct', latestPct.toString())
      } catch {
        /* ignore */
      }
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }

  const handleTreeResizerDoubleClick = () => {
    setTreeColPct(20)
    try {
      localStorage.setItem('detailedView:treeColPct', '20')
    } catch {
      /* ignore */
    }
  }

  const handleValidationChipClick = (issue: ValidationIssue) => {
    const sel = findSelectionForNodeId(issue.nodeId, issue.nodeKind, goalsList)
    if (sel) {
      handleTreeSelect(sel)
    }
    if (isMobileViewport) {
      setMobileTab('proposed')
    }
    const dispatchFocus = () => {
      window.dispatchEvent(
        new CustomEvent('detailedView:focusField', {
          detail: { nodeId: issue.nodeId, field: issue.field },
        }),
      )
    }
    dispatchFocus()
    requestAnimationFrame(() => {
      dispatchFocus()
    })
  }

  const [hasManualConfidenceBoost, setHasManualConfidenceBoost] = useState(false)
  const confidenceDraftLocked = false
  void aiConfidence
  void hasManualConfidenceBoost
  const operationalBannerNote = useMemo(() => {
    const envelope = aiOperationalProposal?.operationEnvelope
    if (!envelope) return null
    const reversible = aiOperationalProposal.rollback?.reversible ? 'reversible' : 'not reversible'
    const affected = envelope.simulation?.affectedRecords.length ?? envelope.operations.flatMap((operation) => operation.targetRefs).length
    return `Operational plan: ${envelope.risk.toLowerCase()} risk, ${envelope.reviewRequired ? 'approval required' : 'approval optional'}, ${reversible}${affected ? `, ${affected} affected record${affected === 1 ? '' : 's'}` : ''}.`
  }, [aiOperationalProposal])

  // If the user leaves edit mode while parked on the Baseline tab (which only
  // exists in edit mode), fall back to Proposed.
  useEffect(() => {
    setMobileTab((current) => (current === 'baseline' && mode !== 'edit' ? 'proposed' : current))
  }, [mode])
  // Current in-flight drag payload — set on dragstart, cleared on drop. Used
  // to validate hover targets without round-tripping through the dataTransfer.
  const dragPayloadRef = useRef<ChecklistDragPayload | null>(null)

  // (Re)hydrate state ONLY when the screen opens or a NEW goal is loaded.
  // We deliberately do *not* clear session-tracking maps when workingGoal
  // becomes null mid-session: a "Discard all (incl. session)" sets workingGoal
  // to null and we still need the rollback to consult the snapshots. The maps
  // are cleared after rollback completes, or on the next fresh open.
  const lastInitializedGoalIdRef = useRef<string | null>(null)
  // For workspace mode, key on the joined goal ids so we re-init when the
  // workspace's goal list changes.
  const goalIdsKey = goalsList.map((goal) => goal.id).join('|')

  useEffect(() => {
    if (!isOpen) {
      lastInitializedGoalIdRef.current = null
      return
    }
    if (goalsList.length === 0) return
    const initKey = isWorkspaceMode ? `ws:${goalIdsKey}` : goalsList[0].id
    if (lastInitializedGoalIdRef.current === initKey) return

    lastInitializedGoalIdRef.current = initKey
    setSelection(initialFocus ?? { kind: 'goal', goalId: goalsList[0].id })

    // Asterisk semantics:
    //   - generate mode: every node is freshly created → every node is
    //     unsaved AND highlighted as draft changes.
    //   - edit mode: only nodes the AI actually changed (aiChangedIds) get
    //     the asterisk + draft-attention flag. Untouched nodes read as clean.
    //   - detail / workspace mode: nothing is unsaved until the user edits.
    if (mode === 'generate' && goalsList[0]) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUnsavedIds(new Set(collectAllGoalLikeIds(goalsList[0])))
      setDraftAttentionIds(new Set(collectAllGoalLikeIds(goalsList[0])))
    } else if (mode === 'edit') {
      const changed = new Set(aiChangedIds ?? [])
      setUnsavedIds(changed)
      setDraftAttentionIds(changed)
    } else {
      setUnsavedIds(new Set())
      setDraftAttentionIds(new Set())
    }

    // In edit/workspace mode, every existing baseline node is already
    // committed in Firestore — pre-seed savedNodeIds so the top-down guard
    // lets the user save children straight away.
    const preSaved = new Set<string>()
    if (mode === 'edit' || isWorkspaceMode) {
      for (const goal of baselineList) {
        for (const id of collectAllGoalLikeIds(goal)) preSaved.add(id)
      }
    }
    setSavedNodeIds(preSaved)
    setMarkedForSaveIds(new Set())
    setMarkedSaveSnapshots(new Map())
    setSavedElementIdsByLocal({})
    setSessionUndoSnapshots({})
    setBannerNote([aiNote, operationalBannerNote].filter(Boolean).join('\n') || null)
    setHasManualConfidenceBoost(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, goalIdsKey, mode, aiChangedIds, aiNote, operationalBannerNote])

  // ---- Live validation -------------------------------------------------
  // Re-run validators on every change to the working goal. The initial set
  // (from initialValidationIssues prop) gets replaced by the live result on
  // first render; we never read the prop after that.
  // Validation runs on every render where the goal contents changed. The
  // immutable updates we make mean a real edit always produces a fresh
  // goalsList reference, so a useMemo here is correct (no useEffect tick).
  const validationIssues = useMemo<ValidationIssue[]>(() => {
    if (goalsList.length === 0) return []
    const issues: ValidationIssue[] = []
    for (const goal of goalsList) issues.push(...validateGoalTree(goal))
    return issues
  }, [goalsList])
  // Keep the state setter shape callers expect for the rare path that
  // mutates it explicitly. Setter is a no-op now since the value is derived.
  const setValidationIssues = (_: ValidationIssue[]) => {
    /* derived from goalsList — no manual override needed. */
    void _
  }
  void setValidationIssues
  // Suppress unused-prop lint while still accepting it as part of the API.
  void initialValidationIssues

  const invalidIds = useMemo(() => {
    const set = new Set<string>()
    for (const issue of validationIssues) set.add(issue.nodeId)
    return set
  }, [validationIssues])
  const isAnythingInvalid = invalidIds.size > 0

  // Single tree (non-workspace) vs roots array (workspace). Pick the right
  // one based on mode; FolderTree accepts both.
  const tree = useMemo(
    () =>
      isWorkspaceMode
        ? null
        : buildTree(workingGoal, baselineGoal, unsavedIds, invalidIds, draftAttentionIds, newMarkerIds, editedMarkerIds),
    [isWorkspaceMode, workingGoal, baselineGoal, unsavedIds, invalidIds, draftAttentionIds, newMarkerIds, editedMarkerIds],
  )
  const workspaceTreeRoots = useMemo(
    () =>
      isWorkspaceMode
        ? buildWorkspaceTree(goalsList, baselineList, unsavedIds, invalidIds, draftAttentionIds, newMarkerIds, editedMarkerIds)
        : null,
    [isWorkspaceMode, goalsList, baselineList, unsavedIds, invalidIds, draftAttentionIds, newMarkerIds, editedMarkerIds],
  )

  // ---- Board-folder grouping (Manage chrome only) -----------------------
  // Map each goal id -> its board so the workspace tree can be partitioned
  // into the top-level board folders. In personal mode there's only DASHBOARD,
  // so the partition still works — the other folders just stay empty (and we
  // suppress them in render).
  const goalIdToBoard = useMemo(() => {
    const map = new Map<string, TimelineBoardKey>()
    for (const element of elements) {
      if (element.type !== 'GOAL') continue
      map.set(element.id, (element.board ?? 'DASHBOARD') as TimelineBoardKey)
    }
    return map
  }, [elements])

  const boardGroups = useMemo<BoardGroup[] | null>(() => {
    if (chrome !== 'manage' || !isWorkspaceMode || !workspaceTreeRoots) return null
    const boardsToShow: TimelineBoardKey[] =
      appMode === 'PERSONAL'
        ? ['DASHBOARD']
        : ['VISIONBOARD', 'DASHBOARD', 'BIN', 'ARCHIVE']
    const byBoard = new Map<TimelineBoardKey, TreeNode[]>()
    for (const key of boardsToShow) byBoard.set(key, [])
    for (const root of workspaceTreeRoots) {
      const board = goalIdToBoard.get(root.id) ?? 'DASHBOARD'
      const bucket = byBoard.get(board)
      if (bucket) bucket.push(root)
    }
    const boardChecklistRoots = new Map<TimelineBoardKey, TreeNode[]>()
    boardsToShow.forEach((board) => boardChecklistRoots.set(board, []))
    checklistItems
      .filter((item) => item.workspaceId === activeWorkspaceId)
      .filter((item) => !item.deletedAt)
      .filter((item) => !item.isRecurrenceInstance)
      .forEach((item) => {
        const board = (item.board ?? 'DASHBOARD') as TimelineBoardKey
        const rootId = checklistRootId(activeWorkspaceId ?? '', board)
        if (item.parentContainerId !== rootId) return
        const bucket = boardChecklistRoots.get(board)
        if (!bucket) return
        bucket.push(buildChecklistTreeNode(item, rootId, invalidIds))
      })
    boardChecklistRoots.forEach((nodes) => {
      nodes.sort((left, right) => {
        const leftY = left.selection.kind === 'checklist'
          ? checklistItems.find((item) => item.id === left.id)?.position?.y ?? 0
          : 0
        const rightY = right.selection.kind === 'checklist'
          ? checklistItems.find((item) => item.id === right.id)?.position?.y ?? 0
          : 0
        if (leftY !== rightY) return leftY - rightY
        const leftX = left.selection.kind === 'checklist'
          ? checklistItems.find((item) => item.id === left.id)?.position?.x ?? 0
          : 0
        const rightX = right.selection.kind === 'checklist'
          ? checklistItems.find((item) => item.id === right.id)?.position?.x ?? 0
          : 0
        if (leftX !== rightX) return leftX - rightX
        return left.title.localeCompare(right.title)
      })
    })
    return boardsToShow.map((key) => ({
      board: key,
      label: getManageBoardLabel(t, key),
      roots: [...(boardChecklistRoots.get(key) ?? []), ...(byBoard.get(key) ?? [])],
      defaultExpanded: key === activeBoard,
    }))
  }, [activeBoard, activeWorkspaceId, appMode, checklistItems, chrome, goalIdToBoard, invalidIds, isWorkspaceMode, t, workspaceTreeRoots])

  const handleAcceptNode = useCallback((nodeId: string) => {
    setDraftAttentionIds((current) => {
      if (!current.has(nodeId)) return current
      const next = new Set(current)
      next.delete(nodeId)
      return next
    })
  }, [])

  // ---- Clipboard handlers ----------------------------------------------

  /** Common copy/cut entry point. Returns true if it set the clipboard. */
  const captureClipboardFrom = useCallback(
    (op: 'copy' | 'cut', target: DetailedSelection | null): boolean => {
      if (!target) return false
      const isManageWorkspace = chrome === 'manage' && isWorkspaceMode
      if (target.kind === 'goal' && !isManageWorkspace) {
        showToast(
          op === 'cut'
            ? 'Goals can’t be cut from here — they’re the root.'
            : 'Goals can’t be copied from here. Copy a task / subtask / checklist instead.',
          'warning',
        )
        return false
      }
      // Resolve sourceBoard from the goal's current location; falls back to
      // activeBoard when the map hasn't caught up yet (newly added goal).
      const sourceBoard =
        (chrome === 'manage' && isWorkspaceMode
          ? nodeBoardOverrides.get(target.goalId) ?? goalIdToBoard.get(target.goalId)
          : activeBoard) ?? activeBoard
      const entry = resolveClipboardEntry(target, goalsList, op, sourceBoard)
      if (!entry) {
        showToast('Could not capture the selected item.', 'warning')
        return false
      }
      setClipboardEntry(entry)
      showToast(op === 'copy' ? 'Copied to clipboard.' : 'Cut to clipboard.', 'info')
      return true
    },
    [activeBoard, chrome, goalIdToBoard, goalsList, isWorkspaceMode, nodeBoardOverrides, showToast],
  )

  const captureClipboard = useCallback(
    (op: 'copy' | 'cut'): boolean => captureClipboardFrom(op, selection),
    [captureClipboardFrom, selection],
  )

  const performPaste = useCallback(
    (target: PasteTarget | null): boolean => {
      if (!clipboardEntry || !target) return false
      if (!canPasteAt(clipboardEntry, target)) {
        const noun =
          clipboardEntry.kind === 'goal'
            ? 'goals'
            : clipboardEntry.kind === 'task'
              ? 'tasks'
              : clipboardEntry.kind === 'subtask'
                ? 'subtasks'
                : 'checklist items'
        const targetNoun =
          clipboardEntry.kind === 'goal'
            ? 'a board folder'
            : clipboardEntry.kind === 'task'
              ? 'a goal'
              : clipboardEntry.kind === 'subtask'
                ? 'a task'
                : 'a goal, task, or subtask'
        showToast(`Paste ${noun} under ${targetNoun}.`, 'warning')
        return false
      }
      if (!setWorkingGoals && !setWorkingGoal) return false
      const result = applyPaste(goalsList, clipboardEntry, target, goalIdToBoard)
      if (!result) return false

      if (isWorkspaceMode) {
        if (!setWorkingGoals) return false
        setWorkingGoals(result.nextGoals)
      } else {
        const next = result.nextGoals.find((goal) => goal.id === goalsList[0]?.id)
        if (!next) return false
        setWorkingGoal(next)
      }

      setSelection(result.pastedSelection)
      setUnsavedIds((current) => {
        const next = new Set(current)
        result.newIds.forEach((id) => next.add(id))
        if (result.movedAcrossGoals && clipboardEntry.kind !== 'goal') {
          next.add(clipboardEntry.sourceGoalId)
        }
        return next
      })
      setDraftAttentionIds((current) => {
        const next = new Set(current)
        result.newIds.forEach((id) => next.add(id))
        return next
      })
      if (result.movedAcrossBoards) {
        setNodeBoardOverrides((current) => {
          const next = new Map(current)
          result.newIds.forEach((id) => next.set(id, result.targetBoard))
          return next
        })
      }

      if (clipboardEntry.op === 'cut') setClipboardEntry(null)
      showToast(clipboardEntry.op === 'cut' ? 'Moved.' : 'Pasted.', 'success')
      return true
    },
    [
      clipboardEntry,
      goalsList,
      goalIdToBoard,
      isWorkspaceMode,
      setWorkingGoals,
      setWorkingGoal,
      showToast,
    ],
  )

  const handlePaste = useCallback((): boolean => {
    if (!selection) return false
    return performPaste({ kind: 'selection', selection })
  }, [performPaste, selection])

  // Resolve the id of the element this selection targets — goal/task/subtask
  // only. Returns null for checklist (those live inside their parent).
  const targetIdForSelection = useCallback(
    (sel: DetailedSelection): string | null => {
      if (sel.kind === 'goal') return sel.goalId
      if (sel.kind === 'task') return sel.taskId
      if (sel.kind === 'subtask') return sel.subTaskId
      return null
    },
    [],
  )

  // Resolve current board for a selection by climbing to its owning goal.
  const boardForSelection = useCallback(
    (sel: DetailedSelection): TimelineBoardKey =>
      nodeBoardOverrides.get(sel.goalId) ?? goalIdToBoard.get(sel.goalId) ?? activeBoard,
    [activeBoard, goalIdToBoard, nodeBoardOverrides],
  )

  // Remove the selected node from the working tree (cascading children).
  const dropFromWorkingTree = useCallback(
    (sel: DetailedSelection) => {
      if (sel.kind === 'checklist') return
      const targetId = targetIdForSelection(sel)
      if (!targetId) return
      if (sel.kind === 'goal') {
        if (isWorkspaceMode) {
          if (!setWorkingGoals) return
          setWorkingGoals(goalsList.filter((g) => g.id !== targetId))
        } else {
          setWorkingGoal(null)
        }
        setSelection(null)
        return
      }
      setGoalAt(sel.goalId, (current) => {
        if (sel.kind === 'task') {
          return { ...current, tasks: current.tasks.filter((t) => t.id !== targetId) }
        }
        // subtask
        return {
          ...current,
          tasks: current.tasks.map((task) =>
            task.id === sel.taskId
              ? { ...task, subTasks: task.subTasks.filter((s) => s.id !== targetId) }
              : task,
          ),
        }
      })
      setSelection({ kind: 'goal', goalId: sel.goalId })
    },
    [goalsList, isWorkspaceMode, setGoalAt, setWorkingGoals, setWorkingGoal, targetIdForSelection],
  )

  const performDelete = useCallback(
    async (sel: DetailedSelection) => {
      if (sel.kind === 'checklist') {
        showToast('Checklist items are deleted with their parent.', 'info')
        return
      }
      const targetId = targetIdForSelection(sel)
      if (!targetId) return
      const board = boardForSelection(sel)
      const existsInFirestore = elements.some((el) => el.id === targetId)

      // Newly-added node never saved → just drop from the working tree, no
      // Firestore write needed.
      if (!existsInFirestore) {
        dropFromWorkingTree(sel)
        showToast('Removed.', 'info')
        return
      }

      try {
        if (board === 'BIN') {
          await permanentlyDeleteElement(targetId)
          dropFromWorkingTree(sel)
          showToast('Permanently deleted.', 'success')
        } else {
          await deleteElement(targetId)
          dropFromWorkingTree(sel)
          showToast('Moved to Bin.', 'success')
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Delete failed.'
        showToast(message, 'warning')
      }
    },
    [boardForSelection, deleteElement, dropFromWorkingTree, elements, permanentlyDeleteElement, showToast, targetIdForSelection],
  )

  const handleDelete = useCallback(() => {
    if (!selection) return
    if (selection.kind === 'checklist') {
      showToast('Checklist items are deleted with their parent.', 'info')
      return
    }
    const board = boardForSelection(selection)
    if (board === 'BIN') {
      setPendingPermanentDelete(selection)
      return
    }
    void performDelete(selection)
  }, [boardForSelection, performDelete, selection, showToast])

  const canDeleteSelection =
    selection !== null && selection.kind !== 'checklist' && (chrome === 'manage' && isWorkspaceMode)

  const handleEmptyBoardOpen = useCallback(
    (group: BoardGroup) => {
      showToast(`No items in ${group.label}.`, 'info')
    },
    [showToast],
  )

  // Keyboard shortcuts: Cmd/Ctrl + C/X/V. Active only when Manage is open
  // AND the focused element isn't an input/textarea (don't hijack typing).
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName ?? ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      if (event.key === 'c' || event.key === 'C') {
        event.preventDefault()
        captureClipboard('copy')
      } else if (event.key === 'x' || event.key === 'X') {
        event.preventDefault()
        captureClipboard('cut')
      } else if (event.key === 'v' || event.key === 'V') {
        event.preventDefault()
        handlePaste()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, captureClipboard, handlePaste])

  // Toolbar button enable/disable logic. In Manage chrome (workspace) goals
  // are copy/cut-able for cross-board moves; everywhere else they're not.
  const canCopySelection =
    selection !== null &&
    goalsList.length > 0 &&
    (selection.kind !== 'goal' || (chrome === 'manage' && isWorkspaceMode))
  const canPasteHere =
    clipboardEntry !== null &&
    selection !== null &&
    canPasteAt(clipboardEntry, { kind: 'selection', selection })

  const selectionAsKey = selection ? treeSelectionKey(selection) : null
  const { workingNode, baselineNode } = useMemo(
    () =>
      isWorkspaceMode
        ? resolveSelectionInWorkspace(selection, goalsList, baselineList)
        : resolveSelection(selection, workingGoal, baselineGoal),
    [isWorkspaceMode, selection, goalsList, baselineList, workingGoal, baselineGoal],
  )

  // ---- Right-side change handlers ---------------------------------------

  const markDirty = useCallback((nodeId: string) => {
    setUnsavedIds((current) => {
      if (current.has(nodeId)) return current
      const next = new Set(current)
      next.add(nodeId)
      return next
    })
  }, [])

  const handleAddGoal = useCallback(() => {
    const nextGoal = createManualGoal(goalsList.length)
    if (isWorkspaceMode) {
      if (!setWorkingGoals) return
      setWorkingGoals([...goalsList, nextGoal])
    } else {
      setWorkingGoal(nextGoal)
    }
    setSelection({ kind: 'goal', goalId: nextGoal.id })
    markDirty(nextGoal.id)
    showToast('Goal added. Save it when ready.', 'success')
  }, [goalsList, isWorkspaceMode, setWorkingGoals, setWorkingGoal, markDirty, showToast])

  const handleAddTask = useCallback(() => {
    if (!selection || selection.kind !== 'goal') {
      showToast('Select a goal before adding a task.', 'warning')
      return
    }
    const goal = goalsList.find((candidate) => candidate.id === selection.goalId)
    if (!goal) return
    const nextTask = createManualTask(goal)
    setGoalAt(goal.id, (current) => ({ ...current, tasks: [...current.tasks, nextTask] }))
    setSelection({ kind: 'task', goalId: goal.id, taskId: nextTask.id })
    markDirty(nextTask.id)
    showToast('Task added. Save it when ready.', 'success')
  }, [goalsList, markDirty, selection, setGoalAt, showToast])

  const handleAddSubTask = useCallback(() => {
    if (!selection || (selection.kind !== 'task' && selection.kind !== 'subtask')) {
      showToast('Select a task before adding a subtask.', 'warning')
      return
    }
    const goal = goalsList.find((candidate) => candidate.id === selection.goalId)
    const taskId = selection.taskId
    const task = goal?.tasks.find((candidate) => candidate.id === taskId)
    if (!goal || !task) return
    const nextSubTask = createManualSubTask(task)
    setGoalAt(goal.id, (current) => ({
      ...current,
      tasks: current.tasks.map((currentTask) =>
        currentTask.id === task.id
          ? { ...currentTask, subTasks: [...currentTask.subTasks, nextSubTask] }
          : currentTask,
      ),
    }))
    setSelection({ kind: 'subtask', goalId: goal.id, taskId: task.id, subTaskId: nextSubTask.id })
    markDirty(nextSubTask.id)
    showToast('Subtask added. Save it when ready.', 'success')
  }, [goalsList, markDirty, selection, setGoalAt, showToast])

  const handleAddFolderType = useCallback(
    (type: TimelineElementType) => {
      if (type === 'GOAL') {
        handleAddGoal()
        return
      }
      if (type === 'TASK') {
        handleAddTask()
        return
      }
      handleAddSubTask()
    },
    [handleAddGoal, handleAddSubTask, handleAddTask],
  )

  const showManualAddControls = isWorkspaceMode && chrome === 'manage'

  const handleAddChecklistFile = useCallback(() => {
    if (!showManualAddControls) return
    if (!selection) {
      showToast('Select a goal, task, or subtask before adding a file.', 'warning')
      return
    }
    const goal = goalsList.find((candidate) => candidate.id === selection.goalId)
    if (!goal) return

    const nextSelection = (() => {
      if (selection.kind === 'goal') {
        const nextItem = createManualChecklist(goal.checklist.length)
        setGoalAt(goal.id, (current) => ({
          ...current,
          checklist: [...current.checklist, nextItem],
        }))
        return { kind: 'checklist' as const, goalId: goal.id, checklistId: nextItem.id }
      }

      if (selection.kind === 'task') {
        const task = goal.tasks.find((candidate) => candidate.id === selection.taskId)
        if (!task) return null
        const nextItem = createManualChecklist(task.checklist.length)
        setGoalAt(goal.id, (current) => ({
          ...current,
          tasks: current.tasks.map((currentTask) =>
            currentTask.id === task.id
              ? { ...currentTask, checklist: [...currentTask.checklist, nextItem] }
              : currentTask,
          ),
        }))
        return { kind: 'checklist' as const, goalId: goal.id, taskId: task.id, checklistId: nextItem.id }
      }

      if (selection.kind === 'subtask') {
        const task = goal.tasks.find((candidate) => candidate.id === selection.taskId)
        const subTask = task?.subTasks.find((candidate) => candidate.id === selection.subTaskId)
        if (!task || !subTask) return null
        const nextItem = createManualChecklist(subTask.checklist.length)
        setGoalAt(goal.id, (current) => ({
          ...current,
          tasks: current.tasks.map((currentTask) =>
            currentTask.id === task.id
              ? {
                  ...currentTask,
                  subTasks: currentTask.subTasks.map((currentSubTask) =>
                    currentSubTask.id === subTask.id
                      ? { ...currentSubTask, checklist: [...currentSubTask.checklist, nextItem] }
                      : currentSubTask,
                  ),
                }
              : currentTask,
          ),
        }))
        return {
          kind: 'checklist' as const,
          goalId: goal.id,
          taskId: task.id,
          subTaskId: subTask.id,
          checklistId: nextItem.id,
        }
      }

      const parentSelection: DetailedSelection = selection.subTaskId
        ? { kind: 'subtask', goalId: goal.id, taskId: selection.taskId ?? '', subTaskId: selection.subTaskId }
        : selection.taskId
          ? { kind: 'task', goalId: goal.id, taskId: selection.taskId }
          : { kind: 'goal', goalId: goal.id }
      setSelection(parentSelection)
      return null
    })()

    if (!nextSelection) {
      showToast('Select a goal, task, or subtask before adding a file.', 'warning')
      return
    }
    setSelection(nextSelection)
    markDirty(nextSelection.subTaskId ?? nextSelection.taskId ?? nextSelection.goalId)
    setHasManualConfidenceBoost(true)
    showToast('Checklist file added. Save it when ready.', 'success')
  }, [goalsList, markDirty, selection, setGoalAt, showManualAddControls, showToast])

  const handleCreateRecurringChecklist = useCallback(async () => {
    // Recurring lives in Checklists → Recurring now — one book per parent,
    // Daily or Weekly. This shortcut just points the user there.
    showToast('Recurring lives in Checklists → Recurring. Open that panel to create a new book.', 'info')
  }, [showToast])

  const handleGoalLikeChange = useCallback(
    (next: Goal | Task | SubTask) => {
      if (!selection) return
      setGoalAt(selection.goalId, (current) => updateGoalNode(current, selection, next))
      const nodeId =
        selection.kind === 'goal'
          ? selection.goalId
          : selection.kind === 'task'
            ? selection.taskId
            : selection.kind === 'subtask'
              ? selection.subTaskId
              : selection.goalId
      markDirty(nodeId)
      setHasManualConfidenceBoost(true)
    },
    [selection, setGoalAt, markDirty],
  )

  // ---- Save flow --------------------------------------------------------

  const persistNode = useCallback(
    async (nodeId: string, currentGoal: Goal, runningSavedIds: Record<string, string>): Promise<PersistedDraftNode | null> => {
      // Board priority: an explicit per-node override (set by cross-board
      // paste / drag) wins over the screen-level activeBoard default.
      const boardForNode = nodeBoardOverrides.get(nodeId) ?? activeBoard
      // If a marked-for-save snapshot exists for this node, persist the
      // snapshot — not the latest working state. This locks in the value the
      // user saw when they clicked Save, even if they kept editing.
      const snapshot = markedSaveSnapshots.get(nodeId)
      const goalForInput = snapshot
        ? overrideNodeInGoal(currentGoal, nodeId, snapshot)
        : currentGoal
      const input = buildElementInput(goalForInput, nodeId, runningSavedIds, boardForNode)
      if (!input) {
        showToast('Could not build save input for this item.', 'warning')
        return null
      }

      setSessionUndoSnapshots((current) => {
        if (current[nodeId] !== undefined) return current
        const existing = elements.find((element) => element.id === nodeId) ?? null
        return { ...current, [nodeId]: existing }
      })

      const existingElementId = runningSavedIds[nodeId] ?? elements.find((element) => element.id === nodeId)?.id
      if (existingElementId) {
        const existingElement = elements.find((element) => element.id === existingElementId)
        // For cross-board cut/move the element keeps its id but its board
        // changes — include `board` in the patch so Firestore re-routes it.
        const boardChanged = nodeBoardOverrides.has(nodeId)
        await updateElement(existingElementId, {
          title: input.title,
          description: input.description,
          checklist: input.checklist,
          startDate: input.startDate,
          endDate: input.endDate,
          color: input.color,
          parentId: input.parentId ?? null,
          ...(boardChanged ? { board: boardForNode } : {}),
        })
        return {
          id: existingElementId,
          type: input.type,
          parentId: input.parentId ?? null,
          board: boardForNode,
          routePath: existingElement?.routePath,
        }
      }
      const saved = await createElement(input)
      return saved
    },
    [createElement, updateElement, elements, showToast, activeBoard, nodeBoardOverrides, markedSaveSnapshots],
  )

  /** Map a node id to its owning goal in goalsList. */
  const findOwningGoal = useCallback(
    (nodeId: string): Goal | null => {
      for (const goal of goalsList) {
        if (goal.id === nodeId) return goal
        if (goal.tasks.some((task) => task.id === nodeId)) return goal
        if (goal.tasks.some((task) => task.subTasks.some((sub) => sub.id === nodeId))) return goal
      }
      return null
    },
    [goalsList],
  )

  const persistInOrder = useCallback(
    async (ids: string[]): Promise<PersistBatchResult | null> => {
      if (goalsList.length === 0) return null

      // Group save targets by goal — each goal runs its own top-down ceremony.
      const byGoal = new Map<string, string[]>()
      for (const id of ids) {
        const owner = findOwningGoal(id)
        if (!owner) continue
        const bucket = byGoal.get(owner.id) ?? []
        bucket.push(id)
        byGoal.set(owner.id, bucket)
      }

      let runningElementIds = { ...savedElementIdsByLocal }
      const runningSavedNodeIds = new Set(savedNodeIds)
      const justSaved: string[] = []
      const savedNodesByLocal: Record<string, PersistedDraftNode> = {}

      for (const [goalId, bucketIds] of byGoal) {
        const targetGoal = goalsList.find((goal) => goal.id === goalId)
        if (!targetGoal) continue

        const goalIds = bucketIds.filter((id) => id === targetGoal.id)
        const taskIds = bucketIds.filter((id) => targetGoal.tasks.some((task) => task.id === id))
        const subTaskIds = bucketIds.filter((id) =>
          targetGoal.tasks.some((task) => task.subTasks.some((sub) => sub.id === id)),
        )
        const ordered = [...goalIds, ...taskIds, ...subTaskIds]

        for (const id of ordered) {
          const info = findGoalLikeAncestors(targetGoal, id)
          if (!info) continue
          const guard = canSaveNode(targetGoal, info.kind, id, runningSavedNodeIds)
          if (!guard.allowed) {
            showToast(guard.message, 'warning')
            return null
          }
          const savedElement = await persistNode(id, targetGoal, runningElementIds)
          if (!savedElement) return null
          runningElementIds = { ...runningElementIds, [id]: savedElement.id }
          savedNodesByLocal[id] = savedElement
          runningSavedNodeIds.add(id)
          justSaved.push(id)
        }
      }

      setSavedElementIdsByLocal(runningElementIds)
      setSavedNodeIds(runningSavedNodeIds)
      setUnsavedIds((current) => {
        const next = new Set(current)
        justSaved.forEach((id) => next.delete(id))
        return next
      })
      if (justSaved.length > 0) {
        showToast(
          justSaved.length === 1
            ? t('NLSManageStatusSaved')
            : t('NLSManageStatusSavedCount', { count: justSaved.length }),
          'success',
        )
      }
      return { savedLocalIds: justSaved, savedNodesByLocal }
    },
    [goalsList, findOwningGoal, savedElementIdsByLocal, savedNodeIds, persistNode, showToast, t],
  )

  const handleSave = useCallback(
    async (scope: SaveScope) => {
      if (goalsList.length === 0) return
      // Hard guard — the UI disables Save when invalidations exist, but if
      // something slips through (shortcut, race) refuse here too.
      if (validationIssues.length > 0) {
        showToast(t('NLSManageTitleBlockedFixItems'), 'warning')
        return
      }
      const persistScope = async (ids: string[]) => {
        try {
          await persistInOrder(ids)
        } catch (error) {
          console.warn('[DetailedViewScreen] save failed', error)
          const reason = error instanceof Error && error.message ? error.message : t('NLSManageStatusCouldNotSavePending')
          showToast(t('NLSManageStatusSaveFailed', { reason }), 'warning')
        }
      }
      if (scope.kind === 'all') {
        await persistScope([...unsavedIds])
        return
      }
      if (!selection) return
      if (scope.kind === 'self') {
        const selfId =
          selection.kind === 'goal'
            ? selection.goalId
            : selection.kind === 'task'
              ? selection.taskId
              : selection.kind === 'subtask'
                ? selection.subTaskId
                : null
        if (!selfId) {
          showToast('Checklist items save as part of their parent.', 'info')
          return
        }
        await persistScope([selfId])
        return
      }
      // self-and-descendants — find the owning goal first.
      const owner = goalsList.find((goal) => goal.id === selection.goalId)
      if (!owner) return
      const ids = collectSubtreeIds(selection, owner).filter((id) => unsavedIds.has(id))
      await persistScope(ids)
    },
    [goalsList, selection, unsavedIds, persistInOrder, showToast, validationIssues, t],
  )

  // After successful save, the saved nodes no longer need draft attention.
  // persistInOrder doesn't know about this state — wire it via the unsavedIds
  // delta: any id we just removed from unsavedIds should also drop from
  // draftAttentionIds.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraftAttentionIds((current) => {
      let changed = false
      const next = new Set(current)
      for (const id of current) {
        if (!unsavedIds.has(id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : current
    })
  }, [unsavedIds])

  // ---- Discard flow -----------------------------------------------------

  /** Revert a single goal against its baseline for the given node id set. */
  const revertSingleGoal = (
    working: Goal,
    baseline: Goal | null,
    nodeIds: Set<string>,
    snapshots: Map<string, Goal | Task | SubTask>,
  ): Goal => {
    // Prefer the marked-save snapshot (the user's "save point") over the
    // original baseline when both exist for an id. Falls back to baseline
    // only if no snapshot was captured.
    const goalSnapshot = snapshots.get(working.id) as Goal | undefined
    const goalRevert =
      goalSnapshot ??
      (baseline && nodeIds.has(working.id)
        ? {
            title: baseline.title,
            description: baseline.description,
            startDate: baseline.startDate,
            endDate: baseline.endDate,
            baseColor: baseline.baseColor,
            checklist: baseline.checklist,
          }
        : null)
    return {
      ...working,
      ...(nodeIds.has(working.id) && goalRevert
        ? {
            title: goalRevert.title,
            description: goalRevert.description,
            startDate: goalRevert.startDate,
            endDate: goalRevert.endDate,
            baseColor: (goalRevert as Goal).baseColor ?? working.baseColor,
            checklist: goalRevert.checklist,
          }
        : {}),
      tasks: working.tasks.map((task) => {
        const taskSnapshot = snapshots.get(task.id) as Task | undefined
        const baselineTask = baseline?.tasks.find((candidate) => candidate.id === task.id)
        const taskRevert =
          taskSnapshot ??
          (baselineTask && nodeIds.has(task.id) ? baselineTask : null)
        const nextTask: Task =
          taskRevert && nodeIds.has(task.id)
            ? {
                ...task,
                title: taskRevert.title,
                description: taskRevert.description,
                startDate: taskRevert.startDate,
                endDate: taskRevert.endDate,
                color: taskRevert.color,
                checklist: taskRevert.checklist,
              }
            : task
        return {
          ...nextTask,
          subTasks: nextTask.subTasks.map((sub) => {
            if (!nodeIds.has(sub.id)) return sub
            const subSnapshot = snapshots.get(sub.id) as SubTask | undefined
            const baselineSub = baselineTask?.subTasks.find((candidate) => candidate.id === sub.id)
            const subRevert = subSnapshot ?? baselineSub
            if (!subRevert) return sub
            return {
              ...sub,
              title: subRevert.title,
              description: subRevert.description,
              startDate: subRevert.startDate,
              endDate: subRevert.endDate,
              color: subRevert.color,
              checklist: subRevert.checklist,
            }
          }),
        }
      }),
    }
  }

  const revertNodeToBaseline = useCallback(
    (nodeIds: Set<string>) => {
      if (goalsList.length === 0) return

      // Handle generate-mode (no baselines) — nothing to revert TO, but if
      // every id in scope is in the working goal, blow it away.
      if (baselineList.length === 0) {
        if (!isWorkspaceMode && workingGoal && collectAllGoalLikeIds(workingGoal).every((id) => nodeIds.has(id))) {
          setWorkingGoal(null)
        }
        return
      }

      if (isWorkspaceMode) {
        if (!setWorkingGoals) return
        const nextGoals = goalsList.map((goal) => {
          const baseline = baselineList.find((candidate) => candidate.id === goal.id) ?? null
          return revertSingleGoal(goal, baseline, nodeIds, markedSaveSnapshots)
        })
        setWorkingGoals(nextGoals)
      } else if (workingGoal) {
        const baseline = baselineList.find((candidate) => candidate.id === workingGoal.id) ?? null
        setWorkingGoal(revertSingleGoal(workingGoal, baseline, nodeIds, markedSaveSnapshots))
      }
      setUnsavedIds((current) => {
        const remaining = new Set(current)
        nodeIds.forEach((id) => {
          // Items that have a marked snapshot stay clean (they were already
          // marked-for-save and just got reverted to that save point). Items
          // without a snapshot revert to baseline and lose their unsaved flag.
          remaining.delete(id)
        })
        return remaining
      })
      // markedForSaveIds — keep entries that still have a snapshot (the user
      // discarded back to the save point, not past it); drop ones without.
      setMarkedForSaveIds((current) => {
        const remaining = new Set(current)
        nodeIds.forEach((id) => {
          if (!markedSaveSnapshots.has(id)) remaining.delete(id)
        })
        return remaining
      })
    },
    [goalsList, baselineList, isWorkspaceMode, setWorkingGoals, workingGoal, setWorkingGoal, markedSaveSnapshots],
  )

  /**
   * Roll back every node touched this session. Deletes (snapshot===null,
   * meaning the node was *created* this session) are issued top-down so a
   * single Goal delete cascades its children — we skip descendants whose
   * ancestor is also in the delete list. Updates (snapshot!==null, meaning we
   * mutated a pre-existing element) are applied directly.
   *
   * Takes explicit copies of the maps so the caller can sequence the rollback
   * around state resets without losing the data to a re-render race.
   */
  const rollBackSessionSavesWith = useCallback(
    async (
      snapshots: Record<string, TimelineElement | null>,
      savedElementIds: Record<string, string>,
      goalForAncestry: Goal | null,
    ): Promise<{ deleted: number; restored: number }> => {
      const entries = Object.entries(snapshots)
      if (entries.length === 0) return { deleted: 0, restored: 0 }

      // Split entries into deletes (new this session) vs updates (pre-existing).
      const toDeleteLocalIds: string[] = []
      const toUpdate: Array<[string, TimelineElement]> = []
      for (const [localId, snapshot] of entries) {
        if (snapshot === null) toDeleteLocalIds.push(localId)
        else toUpdate.push([localId, snapshot])
      }

      // For deletes: prune descendants whose ancestor is also being deleted, so
      // the cascade in TimelineContext.deleteElement handles them in one batch.
      const toDeleteSet = new Set(toDeleteLocalIds)
      const isCoveredByAncestorDelete = (id: string): boolean => {
        if (!goalForAncestry) return false
        const info = findGoalLikeAncestors(goalForAncestry, id)
        if (!info) return false
        return info.ancestors.some((ancestorId) => toDeleteSet.has(ancestorId))
      }
      const topLevelDeletes = toDeleteLocalIds.filter((id) => !isCoveredByAncestorDelete(id))

      let deleted = 0
      for (const localId of topLevelDeletes) {
        const elementId = savedElementIds[localId] ?? localId
        try {
          await deleteElement(elementId)
          deleted += 1
        } catch (error) {
          console.warn('[detailed-view] rollback delete failed', { localId, elementId, error })
        }
      }

      let restored = 0
      for (const [localId, snapshot] of toUpdate) {
        const elementId = savedElementIds[localId] ?? localId
        try {
          await updateElement(elementId, {
            title: snapshot.title,
            description: snapshot.description,
            checklist: snapshot.checklist,
            startDate: snapshot.startDate,
            endDate: snapshot.endDate,
            color: snapshot.color,
          })
          restored += 1
        } catch (error) {
          console.warn('[detailed-view] rollback update failed', { localId, elementId, error })
        }
      }

      return { deleted, restored }
    },
    [deleteElement, updateElement],
  )

  // Confirm dialog flow: every discard pick stages a PendingDiscard; the
  // dialog's Confirm button runs the actual revert.
  const handleDiscard = useCallback(
    (scope: DiscardScope) => {
      // Per-goal copy of the selection for prompt copy. We pass the first goal
      // for context — the dialog text only uses it for cascade-noun wording.
      setPendingDiscard(buildDiscardPrompt(scope, goalsList[0] ?? null, selection, t))
    },
    [goalsList, selection, t],
  )

  const collectAllIdsAcrossWorkspace = useCallback((): Set<string> => {
    const set = new Set<string>()
    for (const goal of goalsList) {
      for (const id of collectAllGoalLikeIds(goal)) set.add(id)
    }
    return set
  }, [goalsList])

  const executeDiscard = useCallback(async () => {
    const staged = pendingDiscard
    setPendingDiscard(null)
    if (!staged || goalsList.length === 0) return

    if (staged.scope.kind === 'all-unsaved') {
      revertNodeToBaseline(collectAllIdsAcrossWorkspace())
      showToast(t('NLSManageStatusPendingDiscarded'), 'info')
      return
    }
    if (staged.scope.kind === 'all-session') {
      const snapshotsForRollback = sessionUndoSnapshots
      const elementIdsForRollback = savedElementIdsByLocal
      // For ancestry resolution during rollback we need *some* goal — pick the
      // first one as a representative; rollback iterates its own ids regardless.
      const goalForAncestry = goalsList[0]
      const result = await rollBackSessionSavesWith(
        snapshotsForRollback,
        elementIdsForRollback,
        goalForAncestry,
      )
      revertNodeToBaseline(collectAllIdsAcrossWorkspace())
      setSessionUndoSnapshots({})
      setSavedElementIdsByLocal({})
      setSavedNodeIds(new Set())
      setMarkedForSaveIds(new Set())
      setMarkedSaveSnapshots(new Map())
      if (result.deleted + result.restored === 0) {
        showToast(t('NLSManageStatusPendingDiscardedNothingSaved'), 'info')
      } else {
        showToast(
          `${t('NLSManageStatusDiscarded')} Removed ${result.deleted} saved item${result.deleted === 1 ? '' : 's'}` +
            (result.restored > 0 ? `, restored ${result.restored}.` : '.'),
          'info',
        )
      }
      setSelection((currentSel) => ensureValidSelection(currentSel, goalsList))
      return
    }
    // self — always cascades into descendants now. Find the owning goal first.
    if (!selection) return
    const owner = goalsList.find((goal) => goal.id === selection.goalId)
    if (!owner) return
    const ids = collectSubtreeIds(selection, owner)
    revertNodeToBaseline(new Set(ids))
    setMarkedForSaveIds((current) => {
      const next = new Set(current)
      ids.forEach((id) => next.delete(id))
      return next
    })
    showToast(t('NLSManageStatusReverted'), 'info')
    setSelection((currentSel) => ensureValidSelection(currentSel, goalsList))
  }, [
    pendingDiscard,
    goalsList,
    selection,
    revertNodeToBaseline,
    rollBackSessionSavesWith,
    sessionUndoSnapshots,
    savedElementIdsByLocal,
    collectAllIdsAcrossWorkspace,
    showToast,
    t,
  ])

  // Register with ManageDraftContext so the Manage-wide leave-guard dialog
  // (fired on view switch, logout, tab close) sees Review's local unsaved
  // state alongside Board's ManageDraft records. Only relevant in Manage
  // chrome; safe to no-op outside.
  const manageDraftForRegistration = useOptionalManageDraft()
  const isManageChromeForRegistration = chrome === 'manage'
  const reviewHasUnsaved = unsavedIds.size > 0
  // H-1: keep Review in step with live data. The draft is seeded once when
  // Manage opens; checklist items that load or change later must still land.
  const liveSyncDecision = decideLiveReviewSync({
    enabled: isWorkspaceMode && isManageChromeForRegistration && Boolean(onRebaseToLive),
    hasUnsaved: reviewHasUnsaved,
    workingGoals,
    liveGoals,
  })
  useEffect(() => {
    if (liveSyncDecision !== 'rebase' || !liveGoals || !onRebaseToLive) return
    onRebaseToLive(liveGoals)
  }, [liveSyncDecision, liveGoals, onRebaseToLive])
  useEffect(() => {
    if (!manageDraftForRegistration || !isManageChromeForRegistration) return
    return manageDraftForRegistration.registerExternalPendingSource('manage.review', {
      hasUnsaved: reviewHasUnsaved,
      saveAll: () => handleSave({ kind: 'all' }),
      discardAll: () => handleDiscard({ kind: 'all-session' }),
    })
  }, [manageDraftForRegistration, isManageChromeForRegistration, reviewHasUnsaved, handleSave, handleDiscard])

  // Lease lifecycle for Review's local pending set. Diff-based to keep
  // Firestore writes to one per new / cleared unsaved id. Only runs in
  // Manage chrome — non-Manage embeds (empty right now) don't participate.
  useEffect(() => {
    if (!isManageChromeForRegistration) return
    const held = heldLeasesRef.current
    const toAcquire: string[] = []
    const toRelease: string[] = []
    unsavedIds.forEach((id) => { if (!held.has(id)) toAcquire.push(id) })
    held.forEach((id) => { if (!unsavedIds.has(id)) toRelease.push(id) })
    if (toAcquire.length === 0 && toRelease.length === 0) return
    heldLeasesRef.current = new Set(unsavedIds)
    toAcquire.forEach((id) => {
      acquireEditLease(id).catch((error) => {
        console.warn('[DetailedViewScreen] acquireEditLease failed', id, error)
      })
    })
    toRelease.forEach((id) => {
      releaseLease(id).catch((error) => {
        console.warn('[DetailedViewScreen] releaseLease failed', id, error)
      })
    })
  }, [unsavedIds, isManageChromeForRegistration, acquireEditLease, releaseLease])

  useEffect(() => {
    return () => {
      const held = heldLeasesRef.current
      if (held.size === 0) return
      held.forEach((id) => {
        releaseLease(id).catch((error) => {
          console.warn('[DetailedViewScreen] releaseLease on unmount failed', id, error)
        })
      })
      heldLeasesRef.current = new Set()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Drag and drop ------------------------------------------------------

  const handleChecklistDragStart = useCallback(
    (sel: DetailedSelection & { kind: 'checklist' }): ChecklistDragPayload | null => {
      const owner = goalsList.find((goal) => goal.id === sel.goalId)
      if (!owner) return null
      const payload = buildDragPayload(owner, sel)
      dragPayloadRef.current = payload
      return payload
    },
    [goalsList],
  )

  const handleValidateDrop = useCallback((target: ChecklistDropTarget): boolean => {
    const payload = dragPayloadRef.current
    if (!payload) return false
    return isValidDrop(payload, target)
  }, [])

  // ---- Cross-board / re-parent drag (goal/task/subtask) ------------------
  // The drag stashes the dragged selection in a ref; on drop we synthesize a
  // clipboard entry (op = cut for plain, copy for Alt) and run performPaste.
  const nodeDragRef = useRef<NodeDragPayload | null>(null)

  const handleNodeDragStart = useCallback(
    (sel: DetailedSelection, altKey: boolean): NodeDragPayload | null => {
      const goal = goalsList.find((g) => g.id === sel.goalId)
      if (!goal) return null
      // In Manage chrome the source board comes from the canonical map (or any
      // pending override from a prior cross-board paste); elsewhere fall back
      // to activeBoard.
      const sourceBoard =
        (chrome === 'manage' && isWorkspaceMode
          ? nodeBoardOverrides.get(sel.goalId) ?? goalIdToBoard.get(sel.goalId)
          : activeBoard) ?? activeBoard
      const payload: NodeDragPayload = {
        fromSelection: sel,
        sourceBoard,
        op: altKey ? 'copy' : 'cut',
      }
      nodeDragRef.current = payload
      return payload
    },
    [activeBoard, chrome, goalIdToBoard, goalsList, isWorkspaceMode, nodeBoardOverrides],
  )

  const synthesizeEntryForDrag = useCallback(
    (payload: NodeDragPayload): ClipboardEntry | null =>
      resolveClipboardEntry(payload.fromSelection, goalsList, payload.op, payload.sourceBoard),
    [goalsList],
  )

  const handleValidateNodeDrop = useCallback(
    (target: PasteTarget): boolean => {
      const payload = nodeDragRef.current
      if (!payload) return false
      const entry = synthesizeEntryForDrag(payload)
      if (!entry) return false
      // No-op guard: dropping a row onto itself.
      if (target.kind === 'selection') {
        const sel = target.selection
        const dragId =
          payload.fromSelection.kind === 'goal'
            ? payload.fromSelection.goalId
            : payload.fromSelection.kind === 'task'
              ? payload.fromSelection.taskId
              : payload.fromSelection.kind === 'subtask'
                ? payload.fromSelection.subTaskId
                : payload.fromSelection.checklistId
        const dropId =
          sel.kind === 'goal'
            ? sel.goalId
            : sel.kind === 'task'
              ? sel.taskId
              : sel.kind === 'subtask'
                ? sel.subTaskId
                : sel.checklistId
        if (dragId === dropId) return false
      }
      return canPasteAt(entry, target)
    },
    [synthesizeEntryForDrag],
  )

  const handleNodeDrop = useCallback(
    (target: PasteTarget) => {
      const payload = nodeDragRef.current
      nodeDragRef.current = null
      if (!payload) return
      const entry = synthesizeEntryForDrag(payload)
      if (!entry) return
      // Seed the clipboard with the synthesized entry — useful for repeated
      // copy-paste after Alt-drag. Cut-drag clears it again below once the
      // paste lands.
      setClipboardEntry(entry)
      // Paste synchronously using the synthesized entry, bypassing the state
      // round-trip in performPaste.
      const result = applyPaste(goalsList, entry, target, goalIdToBoard)
      if (!result) return
      if (isWorkspaceMode) {
        if (!setWorkingGoals) return
        setWorkingGoals(result.nextGoals)
      } else {
        const next = result.nextGoals.find((g) => g.id === goalsList[0]?.id)
        if (!next) return
        setWorkingGoal(next)
      }
      setSelection(result.pastedSelection)
      setUnsavedIds((current) => {
        const out = new Set(current)
        result.newIds.forEach((id) => out.add(id))
        if (result.movedAcrossGoals && entry.kind !== 'goal') out.add(entry.sourceGoalId)
        return out
      })
      setDraftAttentionIds((current) => {
        const out = new Set(current)
        result.newIds.forEach((id) => out.add(id))
        return out
      })
      if (result.movedAcrossBoards) {
        setNodeBoardOverrides((current) => {
          const out = new Map(current)
          result.newIds.forEach((id) => out.set(id, result.targetBoard))
          return out
        })
      }
      // Plain drag (cut) clears the synthesized clipboard; Alt-drag (copy)
      // leaves an entry on the clipboard for the user to keep pasting.
      if (entry.op === 'cut') setClipboardEntry(null)
      showToast(entry.op === 'cut' ? 'Moved.' : 'Copied.', 'success')
    },
    [
      synthesizeEntryForDrag,
      goalsList,
      goalIdToBoard,
      isWorkspaceMode,
      setWorkingGoals,
      setWorkingGoal,
      showToast,
    ],
  )

  // ---- Context menu (right-click on tree rows) ---------------------------
  const handleContextMenu = useCallback(
    (sel: DetailedSelection, position: { clientX: number; clientY: number }) => {
      // Move selection so subsequent paste lands in the right place.
      setSelection(sel)
      setContextMenu({ selection: sel, position })
    },
    [],
  )

  const handleChecklistDrop = useCallback(
    (target: ChecklistDropTarget) => {
      const payload = dragPayloadRef.current
      dragPayloadRef.current = null
      if (!payload) return
      if (!isValidDrop(payload, target)) return
      const owner = goalsList.find((goal) => goal.id === payload.fromGoalId)
      if (!owner) return
      const result = applyChecklistMove(owner, payload, target)
      if (!result) return
      setGoalAt(owner.id, () => result.next)
      setSelection(result.nextSelection)
      const nextChecklistSelection = result.nextSelection as DetailedSelection & { kind: 'checklist' }
      const newParent =
        nextChecklistSelection.subTaskId ?? nextChecklistSelection.taskId ?? nextChecklistSelection.goalId
      const oldParent = payload.fromSubTaskId ?? payload.fromTaskId ?? payload.fromGoalId
      markDirty(newParent)
      if (oldParent !== newParent) markDirty(oldParent)
    },
    [goalsList, setGoalAt, markDirty],
  )

  // ---- Save-gating derivation for the toolbar ---------------------------

  const { selectionSaveBlocked, selectionSaveBlockedReason } = useMemo(() => {
    if (!selection || selection.kind === 'checklist') {
      return { selectionSaveBlocked: false, selectionSaveBlockedReason: null as string | null }
    }
    const owner = goalsList.find((goal) => goal.id === selection.goalId)
    if (!owner) {
      return { selectionSaveBlocked: false, selectionSaveBlockedReason: null }
    }
    const targetId =
      selection.kind === 'goal'
        ? owner.id
        : selection.kind === 'task'
          ? selection.taskId
          : selection.subTaskId
    const info = findGoalLikeAncestors(owner, targetId)
    if (!info) return { selectionSaveBlocked: false, selectionSaveBlockedReason: null }
    const guard = canSaveNode(owner, info.kind, targetId, savedNodeIds)
    return guard.allowed
      ? { selectionSaveBlocked: false, selectionSaveBlockedReason: null }
      : { selectionSaveBlocked: true, selectionSaveBlockedReason: guard.message }
  }, [goalsList, selection, savedNodeIds])

  // Build the set of field names that are invalid on the currently-selected
  // node. The forms thread these into DiffField so the offending input gets a
  // yellow border + ⚠️ tooltip. Declared before the `isOpen` early-return so
  // the hook count stays stable across renders.
  const invalidFieldsForSelection = useMemo<Set<string>>(() => {
    if (!selection) return new Set()
    const targetId =
      selection.kind === 'goal'
        ? selection.goalId
        : selection.kind === 'task'
          ? selection.taskId
          : selection.kind === 'subtask'
            ? selection.subTaskId
            : selection.checklistId
    const fields = new Set<string>()
    for (const issue of validationIssues) {
      if (issue.nodeId === targetId && issue.field) fields.add(issue.field)
    }
    return fields
  }, [selection, validationIssues])

  // ---- Review → shared actions-bar store publish -----------------------
  // showActions must be declared here (before the useMemos that reference it)
  // rather than in the JSX section, because the useMemos run unconditionally
  // before the `if (!isOpen) return null` guard.
  const showActions = mode !== 'detail' || unsavedIds.size > 0
  //
  // When Manage's Review mode is active, Review owns the top chrome action
  // bar. We compute the same three button arrays that the now-deleted second
  // <header> row used to render inline, then push them into the module-level
  // store so ManageActionsBar picks them up. Board's own effect is gated
  // to only publish when manageMode === 'board', so there is no race.
  //
  // All three useMemos intentionally mirror the IIFE logic that previously
  // lived inside the JSX return; they must be above the `if (!isOpen) return
  // null` guard so the hook count stays stable.

  const reviewClipboardItems = useMemo<ButtonGroupItem[]>(() => {
    if (mode === 'detail') return []
    const deleteIsPermanent = !!(selection && selection.kind !== 'checklist' && boardForSelection(selection) === 'BIN')
    const items: ButtonGroupItem[] = [
      {
        key: 'copy',
        label: t('NLSManageActionCopy'),
        icon: <ContentCopyRoundedIcon fontSize="inherit" />,
        disabled: !canCopySelection,
        title: t('NLSManageTitleCopyShortcut'),
        onClick: () => captureClipboard('copy'),
      },
      {
        key: 'cut',
        label: t('NLSManageActionCut'),
        icon: <ContentCutRoundedIcon fontSize="inherit" />,
        disabled: !canCopySelection,
        title: t('NLSManageTitleCutShortcut'),
        onClick: () => captureClipboard('cut'),
      },
      {
        key: 'paste',
        label: t('NLSManageActionPaste'),
        icon: <ContentPasteRoundedIcon fontSize="inherit" />,
        disabled: !canPasteHere,
        title: clipboardEntry
          ? canPasteHere
            ? t('NLSManageTitlePasteShortcut')
            : t('NLSManageTitleSelectValidClipboardTarget', { kind: clipboardEntry.kind })
          : t('NLSManageTitleClipboardEmpty'),
        onClick: () => handlePaste(),
      },
    ]
    if (chrome === 'manage' && isWorkspaceMode) {
      items.push({
        key: 'delete',
        label: deleteIsPermanent ? t('NLSManageActionPermanentlyDelete') : t('NLSManageActionMoveToBin'),
        icon: deleteIsPermanent
          ? <DeleteForeverRoundedIcon fontSize="inherit" />
          : <DeleteOutlineRoundedIcon fontSize="inherit" />,
        tone: deleteIsPermanent ? 'danger' : 'default',
        disabled: !canDeleteSelection,
        ariaLabel: t('NLSManageActionDelete'),
        title: selection && selection.kind !== 'checklist'
          ? deleteIsPermanent
            ? t('NLSManageTitlePermanentDeleteCannotUndo')
            : t('NLSManageActionMoveToBin')
          : t('NLSManageTitleDeleteSelectTarget'),
        onClick: handleDelete,
      })
    }
    return items
  }, [
    boardForSelection,
    canCopySelection,
    canDeleteSelection,
    canPasteHere,
    captureClipboard,
    chrome,
    clipboardEntry,
    handleDelete,
    handlePaste,
    isWorkspaceMode,
    mode,
    selection,
    t,
  ])

  const reviewSaveDiscardItems = useMemo(() => {
    if (!showActions) return { saveItems: [] as ButtonGroupItem[], discardItems: [] as ButtonGroupItem[] }

    const blockedReason = confidenceDraftLocked
      ? t('NLSManageTitleBlockedEditField')
      : isAnythingInvalid
        ? t('NLSManageTitleBlockedFixItems')
        : selectionSaveBlockedReason

    const selfBlocked = selectionSaveBlocked || isAnythingInvalid || confidenceDraftLocked
    const bulkBlocked = isAnythingInvalid || confidenceDraftLocked
    const nothingPending = unsavedIds.size === 0

    // Compute per-selection pending state inline (mirrors the IIFE in the header)
    const selectionHasCh = (() => {
      if (!selection) return false
      const owner = goalsList.find((g) => g.id === selection.goalId)
      if (!owner) return false
      if (selection.kind === 'goal') return owner.tasks.length > 0
      if (selection.kind === 'task') {
        const task = owner.tasks.find((c) => c.id === selection.taskId)
        return Boolean(task && task.subTasks.length > 0)
      }
      return false
    })()

    const pendingSubtreeIds = (() => {
      if (!selection) return [] as string[]
      const owner = goalsList.find((g) => g.id === selection.goalId)
      if (!owner) return [] as string[]
      return collectSubtreeIds(selection, owner).filter((id) => unsavedIds.has(id))
    })()

    const selfId = selection?.kind === 'goal'
      ? selection.goalId
      : selection?.kind === 'task'
        ? selection.taskId
        : selection?.kind === 'subtask'
          ? selection.subTaskId
          : null

    const descendantIds = pendingSubtreeIds.filter((id) => id !== selfId)

    const hasPendingSelf = selfId !== null && unsavedIds.has(selfId)
    const hasPendingDescendants = descendantIds.length > 0

    const selectedExternalLock = selfId ? getExternalLock(selfId) : null
    const selectionExternallyLocked = selfId !== null && isLockedByExternalUser(selfId)
    const lockOwnerName = selectedExternalLock?.lockedByName ?? t('NLSManageUserTeammateFallback')

    const saveDiscardLabels = getManageSaveDiscardLabels(t)
    const canSaveSelf = selection !== null && !nothingPending && !selfBlocked && hasPendingSelf && !selectionExternallyLocked
    const canSaveChildrenCalc = selection !== null && selectionHasCh && !bulkBlocked && hasPendingDescendants && !selectionExternallyLocked
    const canSaveAllCalc = !nothingPending && !bulkBlocked
    const canDiscardSelf = selection !== null && pendingSubtreeIds.length > 0 && !selectionExternallyLocked
    const canDiscardAllUnsaved = !nothingPending
    const canDiscardAllSession = Object.keys(sessionUndoSnapshots).length > 0 || !nothingPending

    const saveSelfTitle = !selection
      ? t('NLSManageTitleSelectItemFirst')
      : selectionExternallyLocked
        ? t('NLSManageTitleLockedEditing', { name: lockOwnerName })
        : nothingPending
          ? t('NLSManageTitleNoPending')
          : selfBlocked
            ? blockedReason ?? t('NLSManageTitleSaveParentFirst')
            : !hasPendingSelf
              ? t('NLSManageTitleNoPendingItem')
              : saveDiscardLabels.save

    const saveItems: ButtonGroupItem[] = [
      {
        key: 'save',
        label: saveDiscardLabels.save,
        icon: <SaveRoundedIcon fontSize="inherit" />,
        tone: 'save',
        disabled: !canSaveSelf,
        title: saveSelfTitle,
        onClick: () => void handleSave({ kind: 'self' }),
      },
      {
        key: 'save-children',
        label: saveDiscardLabels.saveAllChildItems,
        icon: <SaveAsRoundedIcon fontSize="inherit" />,
        tone: 'save',
        disabled: !canSaveChildrenCalc,
        title: !selection
          ? t('NLSManageTitleSelectItemFirst')
          : !selectionHasCh
            ? t('NLSManageTitleNoChildItems')
            : !hasPendingDescendants
              ? t('NLSManageTitleNoPendingChildChanges')
              : bulkBlocked
                ? blockedReason ?? t('NLSManageTitleFixBlockedBeforeSave')
                : saveDiscardLabels.saveAllChildItems,
        onClick: () => void handleSave({ kind: 'self-and-descendants' }),
      },
      {
        key: 'save-all',
        label: saveDiscardLabels.saveAll,
        icon: <SaveAltRoundedIcon fontSize="inherit" />,
        tone: 'save',
        disabled: !canSaveAllCalc,
        title: nothingPending
          ? t('NLSManageTitleNoPending')
          : bulkBlocked
            ? blockedReason ?? t('NLSManageTitleFixBlockedBeforeSave')
            : saveDiscardLabels.saveAll,
        onClick: () => void handleSave({ kind: 'all' }),
      },
    ]

    const discardItems: ButtonGroupItem[] = [
      {
        key: 'discard',
        label: saveDiscardLabels.discard,
        icon: <DeleteOutlineRoundedIcon fontSize="inherit" />,
        disabled: !canDiscardSelf,
        title: selectionExternallyLocked
          ? t('NLSManageTitleLockedEditing', { name: lockOwnerName })
          : canDiscardSelf ? saveDiscardLabels.discard : t('NLSManageTitleNoPendingSelection'),
        onClick: () => handleDiscard({ kind: 'self' }),
      },
      {
        key: 'discard-unsaved',
        label: saveDiscardLabels.discardAllUnsaved,
        icon: <DeleteOutlineRoundedIcon fontSize="inherit" />,
        disabled: !canDiscardAllUnsaved,
        title: canDiscardAllUnsaved ? saveDiscardLabels.discardAllUnsaved : t('NLSManageTitleNoPending'),
        onClick: () => handleDiscard({ kind: 'all-unsaved' }),
      },
      {
        key: 'discard-all',
        label: saveDiscardLabels.discardAll,
        icon: <DeleteForeverRoundedIcon fontSize="inherit" />,
        tone: 'danger',
        disabled: !canDiscardAllSession,
        title: canDiscardAllSession ? t('NLSManageTitleDiscardAllSession') : t('NLSManageTitleNothingToDiscard'),
        onClick: () => handleDiscard({ kind: 'all-session' }),
      },
    ]

    return { saveItems, discardItems }
  }, [
    confidenceDraftLocked,
    getExternalLock,
    goalsList,
    handleDiscard,
    handleSave,
    isAnythingInvalid,
    isLockedByExternalUser,
    selectionSaveBlocked,
    selectionSaveBlockedReason,
    selection,
    sessionUndoSnapshots,
    showActions,
    t,
    unsavedIds,
  ])

  // Publish Review's action set to the shared ManageActionsBar store when
  // Manage's Review mode is active. BoardExplorer's effect is gated on
  // manageMode === 'board' so there is no double-publish race.
  useEffect(() => {
    if (chrome !== 'manage' || manageMode !== 'review') {
      return
    }
    setManageActionsBar({
      canEditBoard: showManualAddControls,
      openElement: null, // Review has no drilled-folder concept → always show dropdown
      onCreateElement: (type) => handleAddFolderType(type ?? 'GOAL'),
      onCreateChecklist: handleAddChecklistFile,
      onCreateRecurringChecklist: () => void handleCreateRecurringChecklist(),
      clipboardItems: reviewClipboardItems,
      saveItems: reviewSaveDiscardItems.saveItems,
      discardItems: reviewSaveDiscardItems.discardItems,
      headerControls,
    })
    return () => {
      setManageActionsBar(null)
    }
  }, [
    chrome,
    handleAddChecklistFile,
    handleAddFolderType,
    handleCreateRecurringChecklist,
    headerControls,
    manageMode,
    reviewClipboardItems,
    reviewSaveDiscardItems,
    showManualAddControls,
  ])

  // ---- UI ---------------------------------------------------------------

  if (!isOpen) return null

  const expansionSelection = selection ?? initialFocus
  const treeExpansion =
    isWorkspaceMode && expansionSelection
      ? new Set([
          expansionSelection.goalId,
          ...(expansionSelection.kind !== 'goal' && 'taskId' in expansionSelection
            ? [expansionSelection.taskId ?? '']
            : []),
          ...(expansionSelection.kind === 'subtask' || expansionSelection.kind === 'checklist'
            ? [(expansionSelection as { subTaskId?: string }).subTaskId ?? '']
            : []),
        ].filter(Boolean))
      : undefined


  const renderRightPanel = (column: 'baseline' | 'working') => {
    if (!selection) {
      return <div className="detailedFormEmpty">Select an item from the tree.</div>
    }
    if (selection.kind === 'checklist') {
      const parentElementId = selection.subTaskId ?? selection.taskId ?? selection.goalId
      if (column === 'baseline') {
        return <div className="detailedFormEmpty">Checklist details now open in the shared single-panel editor.</div>
      }
      return (
        <ManageChecklistDetailSurface
          checklistId={selection.checklistId}
          parentContainerId={parentElementId}
          checklistItems={checklistItems}
        />
      )
    }
    const workingItem = workingNode as Goal | Task | SubTask | null
    const baselineItem = baselineNode as Goal | Task | SubTask | null
    const item = column === 'baseline' ? baselineItem : workingItem
    const baseline = column === 'baseline' ? null : baselineItem
    const currentElementId = selection.kind === 'goal'
      ? selection.goalId
      : selection.kind === 'task'
        ? selection.taskId
        : selection.subTaskId
    const rawFlatElement = elements.find((element) => element.id === currentElementId)
    const flatElement = rawFlatElement
      ? {
          ...rawFlatElement,
          ...(liveElementDoc && liveElementDoc.id === rawFlatElement.id ? {
            attachedFormIds: liveElementDoc.attachedFormIds ?? rawFlatElement.attachedFormIds,
            attachedDesignIds: liveElementDoc.attachedDesignIds ?? rawFlatElement.attachedDesignIds,
            attachedUploadIds: liveElementDoc.attachedUploadIds ?? rawFlatElement.attachedUploadIds,
            attachedLinkIds: liveElementDoc.attachedLinkIds ?? rawFlatElement.attachedLinkIds,
            providerMappings: liveElementDoc.providerMappings ?? rawFlatElement.providerMappings,
          } : {}),
        }
      : null
    const attachmentTarget = flatElement
      ? {
          id: flatElement.id,
          workspaceId: flatElement.workspaceId,
          title: flatElement.title,
          kind: flatElement.type,
          collectionName: 'elements' as const,
          attachedFormIds: flatElement.attachedFormIds,
          attachedDesignIds: flatElement.attachedDesignIds,
          attachedUploadIds: flatElement.attachedUploadIds,
          attachedLinkIds: flatElement.attachedLinkIds,
          assigneeIds: flatElement.assignedEditors,
        }
      : null
    const figmaTarget = flatElement?.providerMappings?.figma?.targets?.primary as { url?: unknown } | undefined
    const figmaFileUrl = typeof figmaTarget?.url === 'string' ? figmaTarget.url : null

    const currentGoal = goalsList.find((g) => g.id === selection.goalId) ?? workingGoal
    let parentStart: TimelineDate | null = null
    let parentEnd: TimelineDate | null = null
    if (selection.kind === 'task' && currentGoal) {
      parentStart = currentGoal.startDate ?? null
      parentEnd = currentGoal.endDate ?? null
    } else if (selection.kind === 'subtask' && currentGoal) {
      const parentTask = currentGoal.tasks.find((task) => task.id === selection.taskId) ?? null
      parentStart = parentTask?.startDate ?? null
      parentEnd = parentTask?.endDate ?? null
    }

    const baselineGoalResolved = baselineList.find((g) => g.id === selection.goalId) ?? baselineGoal
    let baselineParentStart: TimelineDate | null = null
    let baselineParentEnd: TimelineDate | null = null
    if (selection.kind === 'task' && baselineGoalResolved) {
      baselineParentStart = baselineGoalResolved.startDate ?? null
      baselineParentEnd = baselineGoalResolved.endDate ?? null
    } else if (selection.kind === 'subtask' && baselineGoalResolved) {
      const parentTask = baselineGoalResolved.tasks.find((task) => task.id === selection.taskId) ?? null
      baselineParentStart = parentTask?.startDate ?? null
      baselineParentEnd = parentTask?.endDate ?? null
    }

    return (
      <div className="detailedFormStack">
        <GoalLikeForm
          item={item}
          baseline={baseline}
          onChange={handleGoalLikeChange}
          readOnly={column === 'baseline'}
          invalidFields={column === 'working' ? invalidFieldsForSelection : undefined}
          parentStart={column === 'baseline' ? baselineParentStart : parentStart}
          parentEnd={column === 'baseline' ? baselineParentEnd : parentEnd}
        />
        {column === 'working' && flatElement && (flatElement.type === 'GOAL' || flatElement.type === 'TASK') ? (
          <div className="detailedViewBudgetWrapper" style={{ marginTop: 12 }}>
            <NodeBudgetLimitCard
              element={flatElement}
              workspace={activeWorkspace}
              role={activeRole}
              canEdit={column === 'working' && (!canPerformOnNode || canPerformOnNode('timeline.edit', flatElement.id))}
            />
          </div>
        ) : null}
        {column === 'working' && flatElement && attachmentTarget ? (
          <>
            <ItemAttachmentsPanel
              target={attachmentTarget}
              budgetLimit={flatElement.budgetLimit}
              estimatedCost={flatElement.estimatedCost}
              canEdit={true}
            />
            {flatElement.type === 'TASK' && (
              <>
                <ExternalLinkSidecard
                  item={{ id: flatElement.id, type: 'TASK', providerMappings: flatElement.providerMappings }}
                  providers={['GITHUB', 'FIGMA']}
                  mode="sidecar"
                  onPush={(provider) => {
                    if (provider === 'FIGMA') {
                      setFigmaTargetTask({ workspaceId: flatElement.workspaceId, elementId: flatElement.id })
                      setFigmaUrlInput('')
                      setFigmaPromptOpen(true)
                      return
                    }
                    void createGitHubIssue(flatElement.workspaceId, flatElement.id, flatElement.title, flatElement.description)
                      .then(() => showToast('GitHub issue created and linked.', 'success'))
                      .catch((error: unknown) => showToast(error instanceof Error ? error.message : 'Could not create GitHub issue.', 'error'))
                  }}
                />
                {figmaFileUrl && <iframe title="Figma file preview" src={`https://www.figma.com/embed?embed_host=timelining&url=${encodeURIComponent(figmaFileUrl)}`} style={{ width: '100%', minHeight: 420, border: 0, borderRadius: 8 }} allowFullScreen />}
              </>
            )}
          </>
        ) : null}
      </div>
    )
  }

  const isManageChrome = chrome === 'manage'
  const showComparisonColumn = mode === 'edit'

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
          className="detailedViewOverlay detailedViewOverlay--page railPageCard detailedViewOverlay--manage"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >


          {liveSyncDecision === 'notify' && (
            <div className="detailedViewBanner" role="status">
              <p>{t('NLSManageReviewLiveUpdatesAvailable')}</p>
              <FilterChip
                size="sm"
                onClick={() => handleDiscard({ kind: 'all-session' })}
              >
                {t('NLSManageReviewLiveUpdatesRefresh')}
              </FilterChip>
            </div>
          )}

          {bannerNote && (
            <div className="detailedViewBanner">
              <p>{bannerNote}</p>
              <IconButton
                variant="tertiary"
                size="sm"
                onClick={() => setBannerNote(null)}
                label="Dismiss"
                icon={<CloseRoundedIcon fontSize="inherit" />}
              />
            </div>
          )}
          {isAnythingInvalid && (
            <div className="detailedViewBanner detailedViewBanner--warning">
              <span className="detailedViewBannerIcon" aria-hidden="true">!</span>
              <span className="detailedViewBannerText">
                {validationIssues.length === 1
                  ? t('NLSValidationBannerNeedAttentionSingular')
                  : t('NLSValidationBannerNeedAttention', { count: validationIssues.length })}
              </span>
              <span className="detailedViewBannerLinks">
                {validationIssues.slice(0, 3).map((issue) => {
                  const sel = findSelectionForNodeId(issue.nodeId, issue.nodeKind, goalsList)
                  const resolved = sel ? (resolveSelectionInWorkspace(sel, goalsList, []).workingNode as { title?: string; text?: string } | null) : null
                  const nodeTitle = resolved?.title || resolved?.text || 'Item'
                  const shortReason = issue.message.replace(/\.$/, '')
                  return (
                    <Button
                      key={`${issue.nodeId}:${issue.field ?? issue.code}`}
                      className="detailedViewBannerLink"
                      variant="link"
                      size="sm"
                      onClick={() => handleValidationChipClick(issue)}
                    >
                      {nodeTitle} — {shortReason}
                    </Button>
                  )
                })}
                {validationIssues.length > 3 ? (
                  <span className="detailedViewBannerMore">
                    {t('NLSValidationBannerMoreCount', { count: validationIssues.length - 3 })}
                  </span>
                ) : null}
              </span>
            </div>
          )}

          {isMobileViewport && (
            <RailChipTabs<'tree' | 'baseline' | 'proposed'>
              ariaLabel="Detailed view sections"
              orientation="horizontal"
              className="detailedViewMobileTabs"
              value={mobileTab}
              onChange={setMobileTab}
              items={[
                { key: 'tree', label: 'Tree' },
                ...(showComparisonColumn ? [{ key: 'baseline' as const, label: 'Baseline' }] : []),
                { key: 'proposed', label: showComparisonColumn ? 'Proposed' : 'Details' },
              ]}
            />
          )}

          <div
            className={`detailedViewBody ${showComparisonColumn ? 'detailedViewBody--threeCol' : 'detailedViewBody--twoCol'}`}
            data-mobile-tab={isMobileViewport ? mobileTab : undefined}
            style={{
              gridTemplateColumns: isMobileViewport
                ? undefined
                : showComparisonColumn
                  ? `${treeColPct}% 4px ${(100 - treeColPct) / 2}% ${(100 - treeColPct) / 2}%`
                  : `${treeColPct}% 4px ${100 - treeColPct}%`,
            }}
          >
            <aside className="detailedViewTreeColumn">
              <FolderTree
                root={tree as TreeNode | null}
                roots={workspaceTreeRoots}
                boardGroups={boardGroups}
                initialExpansion={treeExpansion}
                selectionKey={selectionAsKey}
                onSelect={handleTreeSelect}
                onExpansionChange={setExpandedIds}
                onChecklistDragStart={handleChecklistDragStart}
                onChecklistDrop={handleChecklistDrop}
                validateDrop={handleValidateDrop}
                disableDrag={mode === 'detail' && unsavedIds.size === 0 && !selection}
                onAcceptNode={handleAcceptNode}
                onRequestBinMove={
                  isManageChrome && isWorkspaceMode && appMode !== 'PERSONAL'
                    ? setBinMoveElementId
                    : undefined
                }
                onNodeDragStart={isManageChrome && isWorkspaceMode ? handleNodeDragStart : undefined}
                onNodeDrop={isManageChrome && isWorkspaceMode ? handleNodeDrop : undefined}
                validateNodeDrop={isManageChrome && isWorkspaceMode ? handleValidateNodeDrop : undefined}
                onContextMenu={mode !== 'detail' ? handleContextMenu : undefined}
                onEmptyBoardOpen={isManageChrome && isWorkspaceMode ? handleEmptyBoardOpen : undefined}
                getLockStatus={getLockStatus}
              />
              {contextMenu && (
                <TreeContextMenu
                  position={contextMenu.position}
                  canCopy={
                    contextMenu.selection.kind !== 'goal' ||
                    (chrome === 'manage' && isWorkspaceMode)
                  }
                  canPaste={
                    clipboardEntry !== null &&
                    canPasteAt(clipboardEntry, { kind: 'selection', selection: contextMenu.selection })
                  }
                  onCopy={() => captureClipboardFrom('copy', contextMenu.selection)}
                  onCut={() => captureClipboardFrom('cut', contextMenu.selection)}
                  onPaste={() =>
                    performPaste({ kind: 'selection', selection: contextMenu.selection })
                  }
                  onClose={() => setContextMenu(null)}
                />
              )}
            </aside>
            <div
              className="detailedViewTreeResizer"
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={treeColPct}
              aria-valuemin={16}
              aria-valuemax={32}
              data-dragging={isDraggingTreeResizer ? 'true' : undefined}
              onPointerDown={handleTreeResizerPointerDown}
              onDoubleClick={handleTreeResizerDoubleClick}
            />
            {showComparisonColumn ? (
              <>
                <section className="detailedViewBaselineColumn">
                  <header className="detailedViewColumnHeader">Before edit</header>
                  {selection
                    ? renderRightPanel('baseline')
                    : <div className="detailedFormEmpty">Select an item to see its previous state.</div>}
                </section>
                <section className="detailedViewProposedColumn">
                  <header className="detailedViewColumnHeader">Proposed</header>
                  {selection && (workingNode || baselineNode)
                    ? renderRightPanel('working')
                    : <div className="detailedFormEmpty">No proposed changes.</div>}
                </section>
              </>
            ) : (
              <section className="detailedViewProposedColumn detailedViewProposedColumn--wide">
                {renderRightPanel('working')}
              </section>
            )}
          </div>

          {binMoveElementId && (
            <BinMoveDialog
              elementId={binMoveElementId}
              onClose={() => setBinMoveElementId(null)}
            />
          )}
          {pendingDiscard && (
            <ConfirmDialog
              title={pendingDiscard.title}
              body={pendingDiscard.body}
              onConfirm={executeDiscard}
              onCancel={() => setPendingDiscard(null)}
            />
          )}
          {pendingPermanentDelete && (
            <ConfirmDialog
              title={t('NLSManageDialogPermanentDeleteTitle')}
              body={t('NLSManageDialogPermanentDeleteBody')}
              confirmLabel={t('NLSManageActionDeleteForever')}
              cancelLabel={t('NLSCommonActionCancel')}
              onConfirm={() => {
                const sel = pendingPermanentDelete
                if (!sel) return
                return performDelete(sel).then(() => setPendingPermanentDelete(null))
              }}
              onCancel={() => setPendingPermanentDelete(null)}
            />
          )}
          {figmaPromptOpen && (
            <AppDialog
              open={figmaPromptOpen}
              onDismiss={() => {
                setFigmaPromptOpen(false)
                setFigmaTargetTask(null)
                setFigmaUrlInput('')
              }}
              title={t('NLSDetailedViewFigmaDialogTitle')}
              actions={figmaActions}
            >
              <div className="detailedFigmaPromptField">
                <label htmlFor="figma-url-input">
                  {t('NLSDetailedViewFigmaDialogPrompt')}
                </label>
                <TextInput
                  id="figma-url-input"
                  value={figmaUrlInput}
                  onChange={(val) => setFigmaUrlInput(val)}
                  placeholder="https://www.figma.com/file/..."
                  autoFocus
                />
              </div>
            </AppDialog>
          )}
        </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
